import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError, blobErrorMessage } from "@/lib/api";
import { PODBlock } from "@/components/PODBlock";
import { toast } from "sonner";
import { istToday } from "@/lib/istDate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Check, X, Package, Truck, ClipboardCheck, AlertCircle, FileText, RotateCcw, Download, Upload, Pencil, Save, ShieldAlert } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { AccessBlocked } from "@/components/AccessBlocked";

const SC = { "Draft":"bg-zinc-200 text-zinc-700","Pending with Manager":"bg-purple-100 text-purple-800","Pending with Admin":"bg-amber-100 text-amber-800","Pending with Finance":"bg-blue-100 text-blue-800","At Warehouse":"bg-orange-100 text-orange-800","Under Processing":"bg-amber-100 text-amber-800","Ready for Dispatch":"bg-orange-200 text-orange-900","Vehicle Assigned":"bg-teal-100 text-teal-700","Invoice Uploaded":"bg-violet-100 text-violet-800","Dispatched":"bg-teal-200 text-teal-900","Delivered":"bg-emerald-100 text-emerald-800","Closure Pending":"bg-pink-100 text-pink-800","Order Closed":"bg-green-200 text-green-900","Rejected":"bg-red-100 text-red-800","Cancelled":"bg-red-200 text-red-900" };
// FINANCE BATCH 1 (1.4c): four payment status words only
const PC = { "Unpaid":"bg-zinc-100 text-zinc-700","Partially Paid":"bg-amber-100 text-amber-800","Fully Paid":"bg-green-100 text-green-800","Short Paid":"bg-orange-100 text-orange-800" };
const STAGES_FALLBACK = ["Draft","Pending with Manager","Pending with Admin","Pending with Finance","At Warehouse","Under Processing","Ready for Dispatch","Vehicle Assigned","Invoice Uploaded","Dispatched","Delivered","Closure Pending","Order Closed"];
const fmt = (n) => '₹' + new Intl.NumberFormat('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}).format(Math.round((n||0)*100)/100);

class OrderDetailErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("OrderDetailPage crash:", error, info); }
  render() {
    if (this.state.hasError) {
      return <div className="p-8 text-center"><h2 className="text-lg font-bold text-red-600 mb-2">Order page encountered an error</h2><p className="text-sm text-zinc-600 mb-4">{this.state.error?.message || "Unknown error"}</p><pre className="text-xs text-left bg-zinc-100 p-3 rounded max-h-40 overflow-auto mb-4">{this.state.error?.stack}</pre><button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded text-sm">Reload Page</button></div>;
    }
    return this.props.children;
  }
}

function OrderDetailPageInner() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("items");          // FOLLOW-UP 3 (4d): the banner opens Logistics
  const roles = user?.roles || [];
  const role = roles[0] || "";
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const TRANSPORT_TYPES = getValues("transport_types");
  const FREIGHT_TERMS = getValues("freight_payment_terms");
  const PAYMENT_MODES = getValues("payment_modes");
  const DEDUCTION_TYPES = getValues("deduction_types");
  const SHORT_SHIPMENT_REASONS = getValues("short_shipment_reasons");
  const ORDER_STATUSES_CONFIG = getValues("order_statuses");
  const STAGES = ORDER_STATUSES_CONFIG.length > 0
    ? ORDER_STATUSES_CONFIG.filter(s => !["Rejected", "Cancelled"].includes(s))
    : STAGES_FALLBACK;

  const [order, setOrder] = useState(null);
  const [orderRTOs, setOrderRTOs] = useState([]);
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionNote, setActionNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);

  // Fix 5: Warehouse processing fields
  const [batchData, setBatchData] = useState([]);
  const [tentativeDispatch, setTentativeDispatch] = useState("");

  // Fix 6: Logistics fields
  const [logForm, setLogForm] = useState({ transporter:"", vehicle:"", transport_type:"", freight_cost:"", freight_terms:"", lr_number:"", dispatch_date:"", est_delivery:"" });
  const [grnForm, setGrnForm] = useState({ grn_number:"", delivery_date:"", delivered_to:"", full_delivery:true, remarks:"" });
  const [grnCorrecting, setGrnCorrecting] = useState(false);   // BATCH 3 FIX-UP (F3a)
  const [grnSaving, setGrnSaving] = useState(false);

  // Fix 1: Invoice upload fields
  const [invoiceForm, setInvoiceForm] = useState({ invoice_number:"", invoice_date:"", invoice_amount:"", mismatch_reason:"", eway_number:"", eway_validity:"" });
  const [changeRequests, setChangeRequests] = useState([]);   // BATCH 2 (2.5)
  // BATCH 2 (2.6b) — cancelling asks what happens to money already received, explicitly.
  const [cancelForm, setCancelForm] = useState({ open: false, mode: "Cancelled", reason: "", money_action: "", refund_mode: "", refund_reference: "" });

  // PASS 2 ZIP 1 FIX-UP (Fix 4b) — an Admin refused the refund: choose again what happens to
  // the money (advance credit or a NEW refund request). Finance (refunds.pay) or an Admin.
  const settleRefused = async (action) => {
    try {
      const { data: resp } = await api.post(`/refunds/orders/${o._id}/settle-refused`,
        { action, refund_mode: "" });
      toast.success(resp.message || "Settled");
      loadOrder();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not settle the money");
    }
  };
  const [cancelling, setCancelling] = useState(false);
  useEffect(() => {
    const d = order?.delivery_data;
    if (d?.delivery_date || d?.grn_number) {
      setGrnForm(f => ({ ...f, grn_number: d.grn_number || f.grn_number,
                         delivery_date: (d.delivery_date || "").slice(0, 10) || f.delivery_date,
                         delivered_to: d.delivered_to || f.delivered_to,
                         remarks: d.remarks || f.remarks }));
    }
  }, [order?._id, order?.delivery_data?.delivery_date, order?.delivery_data?.grn_number]);

  const loadChangeRequests = async (oid) => {
    try {
      const { data } = await api.get("/order-change-requests", { params: { order_id: oid } });
      setChangeRequests((data.data || []).filter(r => r.status === "Pending"));
    } catch (e) { /* non-blocking */ }
  };
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [ewayFile, setEwayFile] = useState(null);
  const [shortPayForm, setShortPayForm] = useState({ deduction_reason: "", deduction_reference: "", details: "" });
  const [showShortPay, setShowShortPay] = useState(false);
  // CN application state
  const [customerCNs, setCustomerCNs] = useState([]);
  const [selectedCN, setSelectedCN] = useState(null);
  const [cnApplyAmount, setCnApplyAmount] = useState(0);

  // Fix 2: Dispatch confirmation fields
  const [dispatchForm, setDispatchForm] = useState({ dispatch_date: istToday(), cartons_loaded:"", seal_number:"", confirmed: false });
  const [dispatchItems, setDispatchItems] = useState([]);

  // Record Payment form state
  const [payForm, setPayForm] = useState({ amount:"", payment_date: istToday(), payment_mode:"", reference:"", notes:"" });
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [proofFiles, setProofFiles] = useState([]);          // FOLLOW-UP 3 (12c)
  const [proofInputKey, setProofInputKey] = useState(0);     // iteration 107: clear the file box
  const [uploadingProof, setUploadingProof] = useState(false);
  // FOLLOW-UP 3 (5c / 8b)
  const [sameUtr, setSameUtr] = useState({ open: false, message: "", existing: null, reason: "" });
  const [repDialog, setRepDialog] = useState({ open: false, rep_id: "", reason: "" });
  const [salesUsers, setSalesUsers] = useState([]);
  const [deductForm, setDeductForm] = useState({ deduction_type:"", amount:"", reference:"", notes:"", date: istToday() });
  const [addingDeduction, setAddingDeduction] = useState(false);
  const [showDeductForm, setShowDeductForm] = useState(false);
  // BATCH 5 PART B (5.3c): change or dispute a deduction that came from a Blinkit file
  const [overrideFor, setOverrideFor] = useState(null);
  const [savingOverride, setSavingOverride] = useState(false);
  const saveOverride = async () => {
    if (!overrideFor?.reason?.trim()) { toast.error("A reason is required"); return; }
    setSavingOverride(true);
    try {
      const path = overrideFor.action === "dispute" ? "dispute" : "override";
      await api.put(`/orders/${orderId}/deductions/${path}`, {
        reference: overrideFor.reference, action: overrideFor.action,
        amount: Number(overrideFor.amount || 0), reason: overrideFor.reason.trim(),
      });
      toast.success(overrideFor.action === "dispute" ? "Marked disputed" : "Deduction changed");
      setOverrideFor(null);
      loadOrder();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSavingOverride(false); }
  };

  // Fix 3: Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLineItems, setEditLineItems] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editConflict, setEditConflict] = useState("");
  const [accessDenied, setAccessDenied] = useState("");

  // BATCH 5 PART A (A2a): loadOrder awaits one catalogue call per SKU before it sets the pick and
  // dispatch tables. Moving to the next order before that finishes used to write the PREVIOUS
  // order's items onto this one — and Batch 2 bills by the picked quantity, so a wrong pick list
  // cuts a wrong invoice. Every response now checks it still belongs to the order on screen.
  const loadTokenRef = useRef(0);
  const loadOrder = async () => {
    const myToken = ++loadTokenRef.current;
    const stale = () => myToken !== loadTokenRef.current;
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      if (stale()) return;
      setOrder(data);
      setAccessDenied("");
      loadChangeRequests(orderId);          // BATCH 2 (2.5)
      // Init batch data from line items — each SKU gets an array of batch rows
      if (data.line_items) {
        // Pre-fill from warehouse_processing.picked_items when available (Fix: batch data persistence)
        const wpPicked = data.warehouse_processing?.picked_items || [];
        const pickedMap = {};
        for (const pi of wpPicked) {
          if (pi.sku_id) pickedMap[pi.sku_id] = pi;
        }
        if (stale()) return;
        setBatchData(data.line_items.map(li => {
          const saved = pickedMap[li.sku_id];
          if (saved && saved.batches && saved.batches.length > 0 && saved.batches.some(b => b.batch_number)) {
            return {
              sku_id: li.sku_id, sku_name: li.sku_name, ordered_qty: li.quantity,
              batches: saved.batches.map(b => ({ batch_number: b.batch_number || "", mfg_date: b.mfg_date || "", expiry_date: b.expiry_date || "", qty: b.qty ?? li.quantity, comment: b.comment || "" })),
              short_reason: saved.short_reason || "",
            };
          }
          // Fallback: check line_item.batch_number (from dedicated batch update endpoint)
          if (li.batch_number) {
            return {
              sku_id: li.sku_id, sku_name: li.sku_name, ordered_qty: li.quantity,
              batches: [{ batch_number: li.batch_number, mfg_date: "", expiry_date: "", qty: li.quantity, comment: "" }],
              short_reason: "",
            };
          }
          return {
            sku_id: li.sku_id, sku_name: li.sku_name, ordered_qty: li.quantity,
            batches: [{ batch_number: "", mfg_date: "", expiry_date: "", qty: li.quantity, comment: "" }],
            short_reason: "",
          };
        }));
        // Init dispatch items with units_per_master_carton from SKU master
        const dispItems = [];
        for (const li of data.line_items) {
          let upmc = 1;
          let cw = 0;
          try { const skuRes = await api.get(`/catalogue/skus/${li.sku_id}`); upmc = skuRes.data?.units_per_master_carton || 0; cw = parseFloat(skuRes.data?.carton_weight) || 0; } catch (err) { console.error("OrderDetailPage error:", err); }
          dispItems.push({ sku_id: li.sku_id, sku_name: li.sku_name, ordered_qty: li.quantity, dispatch_qty: li.quantity, units_per_master_carton: upmc, carton_weight: cw, short_reason: "" });
        }
        if (stale()) return;
        setDispatchItems(dispItems);
      }
    } catch (err) {
      if (stale()) return;
      // FX3b item 8 — a scope refusal (A3) is NOT "order not found". Show the plain
      // no-access screen instead of bouncing the user away with a misleading toast.
      if (err.response?.status === 403) { setAccessDenied("forbidden"); return; }
      toast.error("Order not found"); navigate("/orders");
    }
    finally { setLoading(false); }
  };
  useEffect(() => {
    // clear the previous order's pick / dispatch tables before the new one loads (A2a)
    setDispatchItems([]);
    setBatchData([]);
    setOrder(null);
    loadOrder();
  }, [orderId]);
  // Load RTOs linked to this order
  useEffect(() => {
    if (orderId) {
      api.get(`/rtos/by-order/${orderId}`).then(r => setOrderRTOs(r.data || [])).catch(err => console.error("OrderDetailPage error:", err));
      api.get(`/sla/order/${orderId}`).then(r => setSlaData(r.data)).catch(() => setSlaData(null));
    }
  }, [orderId]);
  // Load available CNs for this customer
  useEffect(() => {
    if (order?.customer_id) {
      api.get(`/credit-notes/customer/${order.customer_id}?status=Open,Partially Used`).then(r => setCustomerCNs(r.data || [])).catch(err => console.error("OrderDetailPage error:", err));
    }
  }, [order?.customer_id]);

  const postComment = async () => {
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      const { data: noteEntry } = await api.post(`/orders/${orderId}/notes`, { note: newComment.trim() });
      setOrder(prev => ({
        ...prev,
        instructions_thread: [...(prev.instructions_thread || []), noteEntry],
      }));
      setNewComment("");
    } catch (err) { toast.error("Failed to post comment"); }
    finally { setPostingComment(false); }
  };

  // BATCH 3 FIX-UP (F3a): an Admin correcting the GRN / delivery date after the order is already
  // Delivered. It saves through the existing "delivered" stage and never touches the status.
  const saveGrnCorrection = async () => {
    if (!grnForm.delivery_date) { toast.error("Enter the GRN / delivery date"); return; }
    if (!(grnForm.change_reason || "").trim()) { toast.error("A reason is required to change a saved GRN / Delivery Date."); return; }
    setGrnSaving(true);
    try {
      const { data: saved } = await api.put(`/orders/${orderId}/stage-data`, { stage: "delivered", data: grnForm });
      toast.success("GRN / delivery date corrected");
      // FOLLOW-UP 3 FIX-UP (X4): before the dispatch date is saved, with a warning
      if (saved?.warning) toast.warning(saved.warning, { duration: 8000 });
      setGrnForm(f => ({ ...f, change_reason: "" }));     // iteration 94: never reuse a reason
      setGrnCorrecting(false);
      await loadOrder();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the correction");
    } finally {
      setGrnSaving(false);
    }
  };

  const doAction = async (newStatus, extraData = {}) => {
    if (newStatus === "Rejected" && !actionNote) { toast.error("Rejection reason is required"); return; }
    // FX3b item 3, widened by FX3c — Manager / Admin decisions on ANY DP exception order
    // (rule breach, above-cap discount or FOC) always carry a note
    if ((o?.is_dp_order || [...(o?.other_exceptions || []), ...(o?.all_dp_exceptions || [])].some(e => e && (e.code === "discount_above_limit" || e.code === "discount_above_cap"))) && o?.has_exceptions
      && ["Pending with Manager", "Pending with Admin"].includes(o?.status)
      && ["Pending with Admin", "Pending with Finance", "Rejected"].includes(newStatus)
      && !actionNote.trim()) {
      toast.error("Add a note for this approval");
      return;
    }
    setSaving(true);
    try {
      // FOLLOW-UP 3 (4c): the Delivered details are sent WITH the status, so the delivery data
      // and the status are saved together or not at all. Every other stage keeps its own save.
      let deliveryPayload = null;
      if (extraData.stage_data) {
        if (newStatus === "Delivered" && extraData.stage_data.stage === "delivered") {
          deliveryPayload = extraData.stage_data.data;
        } else {
          await api.put(`/orders/${orderId}/stage-data`, { stage: extraData.stage_data.stage, data: extraData.stage_data.data });
        }
      }
      const { data: statusResp } = await api.put(`/orders/${orderId}/status`, { status: newStatus, note: actionNote, awb_number: logForm.lr_number || "", courier_name: logForm.transporter || "",
        status_change_reason: actionNote,
        delivery_data: deliveryPayload || {} });   /* FIX-UP R2b + FOLLOW-UP 3 (4c) */
      toast.success(`Order updated to ${newStatus}`);
      // FOLLOW-UP 3 FIX-UP (X4): delivered before the dispatch date is saved, with a warning
      if (statusResp?.warning) toast.warning(statusResp.warning, { duration: 8000 });
      // SLA instant notification toast — delayed to stack below status toast
      if (statusResp?.sla_result) {
        const sla = statusResp.sla_result;
        const icons = { green: "\u2713", amber: "\u26a1", red: "\u2717" };
        const colors = { green: "background: #f0fdf4; border-left: 4px solid #16a34a; color: #15803d;", amber: "background: #fffbeb; border-left: 4px solid #d97706; color: #92400e;", red: "background: #fef2f2; border-left: 4px solid #dc2626; color: #991b1b;" };
        const msgs = { green: "You're on time! Keep it up.", amber: "Getting close to the limit.", red: "SLA breached. Try to act faster next time." };
        setTimeout(() => {
          toast(`${icons[sla.result]} ${sla.points > 0 ? '+' : ''}${sla.points} points — ${sla.stage}`, {
            description: `${sla.message}\n${msgs[sla.result]}`,
            duration: 6000,
            style: { cssText: colors[sla.result] + " padding: 12px 16px; font-size: 13px; font-weight: 600;" },
          });
        }, 500);
      }
      loadOrder();
      setActionNote("");
    } catch (err) {
      // PASS 2 (B2): a Reject refused because money is still on the order opens the same
      // settlement dialog the cancel flow uses — three choices, nothing lost.
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "money_action_required") {
        setCancelForm({ open: true, mode: newStatus, reason: actionNote || "", money_action: "",
                        refund_mode: "", refund_reference: "" });
        toast.error(d.message || "Choose what happens to the money received");
      } else {
        toast.error(formatApiError(d));
      }
    }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-20 text-sm text-zinc-400">Loading order...</div>;
  if (accessDenied) return (
    <div className="p-6" data-testid="order-access-denied">
      <AccessBlocked detail="forbidden" testid="order-access-blocked" />
    </div>
  );
  if (!order) return null;

  const o = order;
  const ewayRecommended = (o.taxable_amount || 0) >= 50000;
  const isAdmin = roles.includes("Admin") || roles.includes("admin");
  const grnLocked = Boolean(o?.delivery_data?.delivery_date);  // BATCH 3 (3.3c)
  // FOLLOW-UP 3 (4d)
  const isLogistics = roles.some(r => ["Logistics Manager", "Logistics Coordinator",
                                       "Logistics Executive"].includes(r));
  const isMgr = roles.includes("Sales Manager");
  // E2E-2 P1 — approval power can come from a PERMISSION an Admin granted, not only from the
  // role name "Sales Manager". Without this a permission-only approver saw no buttons at all.
  const perms = user?.permissions || [];
  const canApproveAsManager = isMgr || isAdmin || perms.includes("dp.orders.approve_manager");
  const canApproveAsAdmin = isAdmin || perms.includes("dp.orders.approve_admin");
  const isFin = roles.includes("Finance Manager") || roles.includes("Finance Executive");
  const isFinMgr = roles.includes("Finance Manager");
  const isWh = roles.includes("Warehouse Manager") || roles.includes("Warehouse Executive");
  const isLog = roles.includes("Logistics Manager") || roles.includes("Logistics Coordinator") || roles.includes("Logistics Executive");
  const isInvoicing = roles.includes("Finance — Invoicing Executive");
  const isFinance = isFin || isInvoicing;
  const canInvoice = isFinMgr || isInvoicing || isAdmin;
  const canDispatch = isWh || isLog || isAdmin;
  const canRecordPayment = isFin || isAdmin;
  // BATCH 2 (2.4c) — ticking "Verified by bank" needs finance.verify_bank
  const canVerifyBank = isAdmin || perms.includes("finance.verify_bank") || perms.includes("admin.full_access");

  // E2E-3 (E7) — edit rights come from PERMISSIONS so a custom role works; the old role
  // names stay as a fallback.
  const canEditAny = isAdmin || perms.includes("orders.amend_any");
  const canEditAsMgr = perms.includes("orders.edit") || roles.includes("Sales Manager");
  const canEditOwn = perms.includes("orders.edit_own") || roles.includes("Sales Executive");
  const canEdit = (() => {
    if (canEditAny) return true;
    if (canEditAsMgr && o?.status === "Pending with Manager") return true;
    if (canEditOwn && ["Draft", "Pending with Manager"].includes(o?.status) && o?.created_by === user?._id) return true;
    return false;
  })();

  const startEdit = () => {
    setEditForm({ po_number: o.po_number || "", po_date: o.po_date || "", pi_reference: o.pi_reference || "", order_date: o.order_date || "", order_type: o.order_type || "", special_instructions: o.special_instructions || "", delivery_address: o.delivery_address || null });
    setEditLineItems((o.line_items || []).map(li => ({ ...li })));
    setEditConflict("");
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setEditForm({}); setEditLineItems([]); setEditConflict(""); };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = { ...editForm, expected_version: o.__v ?? 0, expected_updated_at: o.updated_at || "" };
      if (editLineItems.length > 0) { payload.line_items = editLineItems; payload.customer_state = o.customer_state || ""; }
      const { data } = await api.put(`/orders/${orderId}/edit`, payload);
      toast.success("Order updated successfully");
      setEditMode(false);
      setEditConflict("");
      setOrder(data);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      if (err.response?.status === 409 || err.response?.status === 428) {
        setEditConflict(msg);
        setEditMode(false);
        loadOrder();
      }
      toast.error(msg);
    }
    finally { setSavingEdit(false); }
  };

  // FOLLOW-UP 3 (12a/12c) — the files go through the existing document upload, which already
  // checks the type, the magic bytes and the 10 MB limit, then their ids travel with the payment.
  const uploadProofs = async (files) => {
    if (!files || files.length === 0) return [];
    setUploadingProof(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("entity_type", "order_payment_upload");   // FIX-UP 2 (Y3)
        fd.append("document_type", "payment_proof");
        const { data } = await api.post("/documents/upload", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(data._id);
      }
      return ids;
    } finally { setUploadingProof(false); }
  };

  const addProofToPayment = async (paymentId, files) => {
    if (!files || files.length === 0) { toast.error("Choose a file first"); return; }
    try {
      const ids = await uploadProofs(files);
      if (ids.length === 0) return;
      await api.post(`/orders/${orderId}/payments/${paymentId}/proof`, { proof_documents: ids });
      toast.success("Payment proof added");
      loadOrder();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const recordPayment = async () => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid payment amount"); return; }
    if (s !== "Order Closed" && amt > (o.outstanding_balance || 0)) { toast.error(`Amount (${amt}) cannot exceed outstanding balance (${o.outstanding_balance})`); return; }
    if (!payForm.payment_date) { toast.error("Payment date is required"); return; }
    if (!payForm.payment_mode) { toast.error("Payment mode is required"); return; }
    if (payForm.payment_mode !== "Cash" && !payForm.reference) { toast.error("Reference/UTR number is required for non-cash payments"); return; }
    // BATCH 2 (2.2d): warn about the same UTR before saving. On this order it must be confirmed,
    // on other orders it is only a warning.
    let confirmSeparate = false;
    if (payForm.reference) {
      try {
        const { data: uc } = await api.get("/orders/utr-check", { params: { utr: payForm.reference, order_id: orderId } });
        if (uc.blocked_on_this_order) {
          // FOLLOW-UP 3 (5c): this is never saved on a click any more — it goes for approval
          setSameUtr({ open: true, message: uc.message, existing: (uc.same_order_entries || [])[0] || null, reason: "" });
          return;
        } else if ((uc.other_orders || []).length) {
          const list = uc.other_orders.map(x => `${x.order_number}: ${fmt(x.amount)} on ${x.date}`).join("\n");
          if (!window.confirm(`This UTR is also on:\n${list}\n\nRecord this payment anyway?`)) return;
        }
      } catch (e) { /* the server checks again on save */ }
    }
    // BATCH 2 (2.5): a closed order needs a reason, the change goes to an Admin.
    let closedReason = "";
    if (s === "Order Closed") {
      closedReason = window.prompt("This order is closed. Your change needs Admin approval. Reason:") || "";
      if (!closedReason.trim()) { toast.error("A reason is required to request a change on a closed order"); return; }
    }
    setRecordingPayment(true);
    try {
      const proofIds = await uploadProofs(proofFiles);
      const { data: res } = await api.post(`/orders/${orderId}/record-payment`, {
        amount: amt, payment_date: payForm.payment_date, payment_mode: payForm.payment_mode,
        reference_number: payForm.reference, notes: payForm.notes,
        confirm_separate_payment: confirmSeparate, reason: closedReason,
        proof_documents: proofIds,
      });
      if (res?.pending_admin_approval) toast.success(res.message);
      else toast.success("Payment recorded successfully");
      if ((res?.utr_warnings || []).length) toast.warning(`This UTR is also on ${res.utr_warnings.map(x=>x.order_number).join(", ")}`);
      setPayForm({ amount: "", payment_date: istToday(), payment_mode: "", reference: "", notes: "" });
      setProofFiles([]);
      setProofInputKey(k => k + 1);
      loadOrder(); // Reload to see updated balance
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRecordingPayment(false); }
  };

  // FOLLOW-UP 3 (5c) — send the second payment under the same UTR for approval
  const sendSameUtrForApproval = async () => {
    if ((sameUtr.reason || "").trim().length < 10) {
      toast.error("Give a reason of at least 10 characters"); return;
    }
    setRecordingPayment(true);
    try {
      // iteration 108: the screenshot picked before this dialog travels with the request, so the
      // payment the approval writes keeps its proof
      const proofIds = await uploadProofs(proofFiles);
      const { data: res } = await api.post(`/orders/${orderId}/record-payment`, {
        amount: parseFloat(payForm.amount), payment_date: payForm.payment_date,
        payment_mode: payForm.payment_mode, reference_number: payForm.reference,
        notes: payForm.notes, reason: sameUtr.reason.trim(), proof_documents: proofIds,
      });
      toast.success(res?.message || "Sent for approval");
      setSameUtr({ open: false, message: "", existing: null, reason: "" });
      setPayForm({ amount: "", payment_date: istToday(), payment_mode: "", reference: "", notes: "" });
      setProofFiles([]);
      setProofInputKey(k => k + 1);
      loadOrder();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRecordingPayment(false); }
  };

  const loadSalesUsers = async () => {
    try {
      const { data } = await api.get("/users", { params: { status: "active" } });
      const rows = Array.isArray(data) ? data : (data?.data || data?.users || []);
      setSalesUsers(rows.filter(u => (u.roles || []).some(r =>
        ["Sales Executive", "Sales Manager", "DP Agent"].includes(r))));
    } catch (e) { setSalesUsers([]); }
  };

  const saveSalesRep = async () => {
    if (!repDialog.rep_id) { toast.error("Choose a sales rep"); return; }
    if (!(repDialog.reason || "").trim()) { toast.error("A reason is required"); return; }
    try {
      const { data } = await api.put(`/orders/${orderId}/sales-rep`,
        { sales_rep_id: repDialog.rep_id, reason: repDialog.reason.trim() });
      toast.success(`Sales rep changed to ${data.sales_rep_name}`);
      setRepDialog({ open: false, rep_id: "", reason: "" });
      loadOrder();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addDeduction = async () => {
    if (!deductForm.deduction_type) { toast.error("Deduction type is required"); return; }
    const amt = parseFloat(deductForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid deduction amount"); return; }
    if (!deductForm.notes) { toast.error("Notes are required for deductions"); return; }
    setAddingDeduction(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/add-deduction`, deductForm);
      toast.success("Deduction added");
      setDeductForm({ deduction_type: "", amount: "", reference: "", notes: "", date: istToday() });
      setShowDeductForm(false);
      setOrder(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAddingDeduction(false); }
  };

  const roundOffClose = async () => {
    if (!window.confirm(`Write off ${fmt(o.outstanding_balance)} as rounding difference and mark payment complete?`)) return;
    try {
      const { data } = await api.post(`/orders/${orderId}/round-off-close`);
      toast.success("Payment rounded off and closed");
      setOrder(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const canDeduct = isFin || isAdmin;
  const isInvoicingExec = roles.includes("Finance — Invoicing Executive");
  const canAddDeduction = (isFin && !isInvoicingExec) || isAdmin;
  const canRoundOff = isFinMgr || isAdmin;

  const s = o.status;
  const historySet = new Set((o.status_history||[]).map(h=>h.status));
  const curIdx = STAGES.indexOf(s);

  // Smart approval logic: use the server-side has_exceptions flag
  const hasFOC = (o.foc_items || []).length > 0;
  const needsAdminApproval = o.has_exceptions === true || hasFOC;

  // ═══ COMPLETE ACTION BUTTON MAP ═══
  const actions = [];

  // Draft → Submit
  if (s === "Draft" && (isAdmin || isMgr || role === "Sales Executive")) {
    if (o.created_by === user?._id || isAdmin) {
      actions.push({ label: "Submit for Approval", status: "Pending with Manager", variant: "default", icon: Check });
    }
  }

  // Pending with Manager → Smart routing
  if (s === "Pending with Manager" && canApproveAsManager) {
    if (needsAdminApproval && !isAdmin) {
      // Exception order → Manager can ONLY forward to Admin
      actions.push({ label: "Forward to Admin for Approval", status: "Pending with Admin", variant: "default" });
      actions.push({ label: "Reject", status: "Rejected", variant: "destructive", icon: X });
    } else if (!needsAdminApproval) {
      // Standard order → direct to Finance (bypass Admin)
      actions.push({ label: "Approve — Send to Finance", status: "Pending with Finance", variant: "default", icon: Check });
      actions.push({ label: "Reject", status: "Rejected", variant: "destructive", icon: X });
    } else {
      // Admin sees both options always
      actions.push({ label: "Approve — Send to Finance", status: "Pending with Finance", variant: "default", icon: Check });
      actions.push({ label: "Forward to Admin for Approval", status: "Pending with Admin", variant: "outline" });
      actions.push({ label: "Reject", status: "Rejected", variant: "destructive", icon: X });
    }
  }

  // Pending with Admin → Approve / Reject (Admin only)
  if (s === "Pending with Admin" && canApproveAsAdmin) {
    actions.push({ label: "Approve Discount — Send to Finance", status: "Pending with Finance", variant: "default", icon: Check });
    actions.push({ label: "Reject", status: "Rejected", variant: "destructive", icon: X });
  }

  // Pending with Finance → Approve / Reject (Finance)
  if (s === "Pending with Finance" && (isFin || isAdmin)) {
    actions.push({ label: "Approve — Send to Warehouse", status: "At Warehouse", variant: "default", icon: Package });
    actions.push({ label: "Reject", status: "Rejected", variant: "destructive", icon: X });
  }

  // At Warehouse → Start Processing (WH)
  if (s === "At Warehouse" && (isWh || isAdmin)) {
    actions.push({ label: "Start Processing", status: "Under Processing", variant: "default", icon: Package });
  }

  // Under Processing → Ready for Dispatch (WH)
  if (s === "Under Processing" && (isWh || isAdmin)) {
    actions.push({ label: "Mark Ready for Dispatch", status: "Ready for Dispatch", variant: "default", icon: ClipboardCheck });
  }

  // Ready for Dispatch → Assign Vehicle (Logistics only — not Warehouse)
  if (s === "Ready for Dispatch" && (isLog || isAdmin)) {
    actions.push({ label: "Assign Vehicle", status: "Vehicle Assigned", variant: "default", icon: Truck });
  }

  // Vehicle Assigned → Upload Invoice (Finance Invoicing + Finance + Admin) [FIX 1]
  if (s === "Vehicle Assigned" && canInvoice) {
    actions.push({ label: "Confirm Invoice Uploaded — Ready to Dispatch", status: "Invoice Uploaded", variant: "default", icon: Check, stageKey: "invoice" });
  }

  // Invoice Uploaded → Confirm Dispatch (WH + Logistics + Admin) [FIX 2]
  if (s === "Invoice Uploaded" && canDispatch) {
    actions.push({ label: "Confirm Dispatch", status: "Dispatched", variant: "default", icon: Truck, stageKey: "dispatch" });
  }

  // Dispatched → Upload GRN / Mark Delivered (Logistics)
  if (s === "Dispatched" && (isLog || isAdmin)) {
    actions.push({ label: "Upload GRN — Mark Delivered", status: "Delivered", variant: "default", icon: Check });
  }

  // Delivered → Request Closure (Finance, if fully paid)
  if (s === "Delivered" && (isFin || isAdmin) && (o.outstanding_balance||0) <= 0) {
    actions.push({ label: "Request Order Closure", status: "Closure Pending", variant: "outline" });
  }

  // Closure Pending → Close Order (Finance Manager only)
  if ((s === "Closure Pending" || s === "Closure Requested") && (isFinMgr || isAdmin)) {
    actions.push({ label: "Approve Closure — Close Order", status: "Order Closed", variant: "default", icon: Check });
  }

  // Rejected → Resubmit (original puncher)
  if (s === "Rejected" && (o.created_by === user?._id || isAdmin)) {
    actions.push({ label: "Edit & Resubmit", status: "Pending with Manager", variant: "outline" });
  }

  return (
    <div data-testid="order-detail-page">
      {/* Ready to Close banner */}
      {o && (s === "Delivered" || s === "Closure Requested") && (o.payment_status === "Fully Paid" || (o.outstanding_balance !== undefined && o.outstanding_balance !== null && o.outstanding_balance <= 1)) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-3 flex items-center justify-between" data-testid="ready-to-close-banner">
          <p className="text-sm text-emerald-800 font-medium">This order is fully paid (Outstanding: {fmt(o.outstanding_balance || 0)}). Ready to close.</p>
          <div className="flex gap-2">
            {(isFinMgr || isAdmin) && s !== "Order Closed" && <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => { if (window.confirm(`Close Order ${o.order_number}?`)) doAction("Order Closed"); }}>Close Order</Button>}
            {(isInvoicing || isFinance) && s === "Delivered" && <Button size="sm" variant="outline" className="text-xs" onClick={() => doAction("Closure Requested")}>Request Closure</Button>}
          </div>
        </div>
      )}
      {/* FOLLOW-UP 3 (4d) — GRN recorded but the order was never marked Delivered */}
      {(isLogistics || isAdmin) && ["Dispatched", "Shipped", "Invoice Uploaded"].includes(o.status)
        && ((o.delivery_data?.delivery_date) || o.credit_start_source === "Blinkit GRN") && (
        <div className="border border-amber-300 bg-amber-50 p-3 rounded-sm mb-3 flex items-center justify-between"
             data-testid="grn-not-delivered-banner">
          <p className="text-xs text-amber-900" data-testid="grn-not-delivered-text">
            GRN / delivery date is already recorded
            {o.delivery_data?.delivery_date ? ` (${fmtDate(o.delivery_data.delivery_date)})` : ""} but this
            order is still {o.status}. Mark it Delivered.
          </p>
          {o.status === "Dispatched" && (
          <Button size="sm" className="rounded-sm h-7 text-xs" data-testid="banner-mark-delivered"
                  onClick={() => {
                    setTab("logistics");
                    setTimeout(() => document.querySelector('[data-testid="grn-form"]')
                      ?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
                  }}>
            Go to the GRN form
          </Button>
          )}
        </div>
      )}

      {/* Header — LEAD DESK BUILD item 9(a): the banners are no longer siblings of the title
          inside a flex ROW, which squeezed the order number into a thin column. The title row is
          its own flex row; every banner below it is a full-width block. */}
      <div className="mb-4">
        <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/orders")} className="h-8 w-8"><ArrowLeft size={16} /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-2xl font-bold font-mono">{o.order_number}</h1>
            <span className={`px-3 py-1 text-xs font-semibold rounded-sm ${SC[s]||''}`}>{s}</span>
            {o.payment_status && <span className={`px-3 py-1 text-xs font-semibold rounded-sm ${PC[o.payment_status]||''}`}>{o.payment_status}</span>}
            {(o.has_rto || orderRTOs.length > 0) && <span className="px-2 py-1 text-[10px] font-bold bg-red-100 text-red-700 rounded-sm" data-testid="has-returns-badge">HAS RETURNS</span>}
          </div>
          <p className="text-xs text-zinc-500 mt-1">{o.customer_name} &middot; {o.channel_name} &middot; {o.warehouse_name ? `${o.warehouse_name} · ` : ''}{fmtDate(o.order_date)} &middot; Sales Rep: <span data-testid="order-sales-rep">{o.sales_rep_name || o.created_by_name || "Unassigned"}</span> &middot; Punched by {o.created_by_name || "—"}
            {isAdmin && (
              <button className="ml-2 underline text-[11px] text-zinc-600" data-testid="change-sales-rep-btn"
                      onClick={() => { loadSalesUsers(); setRepDialog({ open: true, rep_id: "", reason: "" }); }}>
                Change sales rep
              </button>
            )}{o.po_number ? ` · PO: ${o.po_number}` : ''}{o.pi_reference ? ` · PI: ${o.pi_reference}` : ''}</p>
        </div>
        </div>

        {/* Auto-Reorder Banner */}
        {o.is_auto_reorder && (
          <div className="w-full border border-blue-300 bg-blue-50 rounded-sm p-3 mt-2" data-testid="auto-reorder-banner">
            <p className="text-sm font-semibold text-blue-900">AUTO-GENERATED REORDER — Review quantities before approving.</p>
            <p className="text-xs text-blue-700 mt-0.5">Based on {o.auto_reorder_meta?.weeks_threshold || 2} weeks average throughput. {o.auto_reorder_meta?.triggered_skus?.length || 0} SKU(s) below threshold.</p>
          </div>
        )}

        {/* E2E-2 R-D1/R-D4 — above-limit discount banner. Works for DP and non-DP orders. */}
        {(() => {
          const ax = [...(o.other_exceptions || []), ...(o.all_dp_exceptions || [])]
            .find(e => e && (e.code === "discount_above_limit" || e.code === "discount_above_cap"));
          if (!ax) return null;
          const rows = ax.lines || [];
          return (
            <div className="w-full border border-amber-400 bg-amber-50 rounded-sm p-3 mt-2" data-testid="discount-above-limit-banner">
              <p className="text-sm font-semibold text-amber-900">Discount above limit — needs Admin approval</p>
              <table className="w-full text-xs mt-2">
                <thead><tr className="text-amber-700 text-left"><th>SKU</th><th className="text-right">Limit %</th><th className="text-right">You gave %</th><th className="text-right">Extra ₹</th><th className="pl-2">Reason</th></tr></thead>
                <tbody>
                  {rows.map((l, i) => (
                    <tr key={i} className="text-amber-900" data-testid={`above-limit-line-${i}`}>
                      <td>{l.sku_name || l.sku_id}</td>
                      <td className="text-right font-mono">{l.limit_percent ?? l.max_allowed}%</td>
                      <td className="text-right font-mono">{l.given_percent ?? l.discount_percent}%</td>
                      <td className="text-right font-mono">{fmt(l.extra_discount_value || 0)}</td>
                      <td className="pl-2 italic">{l.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-amber-700 mt-1.5" data-testid="above-limit-approval-state">
                {o.discount_admin_approval?.at
                  ? `Admin approved by ${o.discount_admin_approval.user_name} on ${fmtDateTime(o.discount_admin_approval.at)} — “${o.discount_admin_approval.note}”`
                  : "Approval flow: Manager forwards to Admin → Admin approves → Finance."}
              </p>
              {/* LEAD DESK BUILD item 9(b): the note an approval needs, where the approval is —
                  the same actionNote the buttons below the order read. */}
              {!o.discount_admin_approval?.at && (
                <div className="mt-2 border-t border-amber-200 pt-2">
                  <Label className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Note / Reason — required to approve this discount</Label>
                  <Textarea value={actionNote} onChange={e => setActionNote(e.target.value)}
                    placeholder="Why is this discount being approved?" rows={2}
                    className="mt-1 rounded-sm text-xs bg-white" data-testid="discount-banner-note" />
                </div>
              )}
              {!o.is_dp_order && (
                <div className="mt-3 border-t border-amber-200 pt-2" data-testid="discount-approval-chain">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mb-1">Approval chain</p>
                  {(o.dp_approval_chain || []).length === 0 ? (
                    <p className="text-[11px] text-amber-700" data-testid="discount-approval-chain-empty">Not started</p>
                  ) : (
                    <ol className="space-y-1">
                      {(o.dp_approval_chain || []).map((s, i) => (
                        <li key={i} className="text-[11px] text-amber-900" data-testid={`discount-chain-step-${i}`}>
                          <span className="font-semibold">{s.step}</span>
                          {" — "}{s.user_name || "—"}
                          {" · "}<span className="font-mono">{s.at ? fmtDateTime(s.at) : "—"}</span>
                          {s.note ? <span className="block italic">“{s.note}”</span> : <span className="block text-amber-600 italic">no note</span>}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* FX3 B5, widened by FX3c — every exception that put this DP order into the chain:
            the DP rule breaches AND the above-cap discount / FOC, each with its note */}
        {o.is_dp_order && o.has_exceptions && (
          <div className="w-full border border-orange-300 bg-orange-50 rounded-sm p-3 mt-2" data-testid="dp-exception-banner">
            <p className="text-sm font-semibold text-orange-900">
              Direct Partner order — {(o.all_dp_exceptions || o.dp_exceptions || []).map(e => e.label).join(" · ")}
            </p>
            <ul className="mt-1 space-y-0.5">
              {(o.all_dp_exceptions || o.dp_exceptions || []).map((e, i) => (
                <li key={i} className="text-xs text-orange-800" data-testid={`dp-exception-note-${i}`}>
                  <span className="font-medium">{e.label}:</span>{" "}
                  {/* DP-FIX-1 C — the payment line reads today's payments, not the words
                      frozen when the exception was raised. The original stays in the chain. */}
                  {e.code === "dp_not_full_advance" && o.dp_payment_now ? (
                    o.dp_payment_now.fully_paid ? (
                      <span className="text-emerald-700 font-medium" data-testid="dp-payment-live-line">
                        Full advance received — ₹{o.dp_payment_now.paid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        {o.dp_payment_now.last_payment_date ? ` on ${fmtDate(o.dp_payment_now.last_payment_date)}` : ""}
                      </span>
                    ) : (
                      <span data-testid="dp-payment-live-line">
                        ₹{o.dp_payment_now.paid.toLocaleString("en-IN", { minimumFractionDigits: 2 })} of
                        ₹{o.dp_payment_now.total.toLocaleString("en-IN", { minimumFractionDigits: 2 })} received
                      </span>
                    )
                  ) : e.detail}
                  {e.code === "dp_not_full_advance" && o.dp_payment_now && (
                    <span className="block text-[10px] text-orange-600 italic" data-testid="dp-payment-at-approval">At approval: {e.detail}</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-orange-700 mt-1.5">
              This order needs Manager, then Admin, then Finance approval before it can move.
            </p>
            {/* FX3b item 3 — who did what, when, and the note they left */}
            <div className="mt-3 border-t border-orange-200 pt-2" data-testid="dp-approval-chain">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-orange-700 mb-1">Approval chain</p>
              {(o.dp_approval_chain || []).length === 0 ? (
                <p className="text-[11px] text-orange-700" data-testid="dp-approval-chain-empty">Not started</p>
              ) : (
                <ol className="space-y-1">
                  {(o.dp_approval_chain || []).map((s, i) => (
                    <li key={i} className="text-[11px] text-orange-900" data-testid={`dp-chain-step-${i}`}>
                      <span className="font-semibold">{s.step}</span>
                      {" — "}{s.user_name || "—"}
                      {" · "}<span className="font-mono">{s.at ? fmtDateTime(s.at) : "—"}</span>
                      {s.note ? <span className="block text-orange-800 italic">“{s.note}”</span> : <span className="block text-orange-600 italic">no note</span>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}

        {/* CN Alert Banner — visible to Finance/Admin when intended_cn_applications exist */}
        {(o.intended_cn_applications || []).length > 0 && (isFin || isFinMgr || isInvoicing || isAdmin) && (
          <div className="w-full border border-amber-300 bg-amber-50 rounded-sm p-3 mt-2" data-testid="cn-alert-banner">
            <p className="text-sm font-semibold text-amber-900">
              {(o.intended_cn_applications || []).length === 1
                ? `Credit Note Applied — CN: ${o.intended_cn_applications[0].cn_number}, Value: ${fmt(o.intended_cn_applications[0].amount)}. Please verify with accounts before approving.`
                : `${o.intended_cn_applications.length} Credit Notes Applied — ${o.intended_cn_applications.map(cn => `${cn.cn_number} (${fmt(cn.amount)})`).join(' + ')} = ${fmt(o.intended_cn_applications.reduce((s, cn) => s + (cn.amount || 0), 0))} total. Please verify with accounts before approving.`
              }
            </p>
          </div>
        )}

        {editConflict && (
          <div className="mb-2 rounded-sm border-l-4 border-red-500 bg-red-50 px-3 py-2" data-testid="order-edit-conflict">
            <p className="text-[11px] font-semibold text-red-800 flex items-center gap-1"><ShieldAlert size={12} />{editConflict}</p>
          </div>
        )}

        {/* Action buttons — top right */}
        <div className="flex flex-col md:flex-row gap-2 flex-wrap">          {canEdit && !editMode && (
            <Button variant="outline" onClick={startEdit} className="rounded-sm text-xs h-8 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="edit-order-btn">
              <Pencil size={12} /> Edit Order
            </Button>
          )}
          {editMode && (
            <>
              <Button onClick={saveEdit} disabled={savingEdit} className="rounded-sm text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-edit-btn">
                <Save size={12} /> {savingEdit ? "Saving..." : "Save Changes"}
              </Button>
              <Button variant="outline" onClick={cancelEdit} className="rounded-sm text-xs h-8" data-testid="cancel-edit-btn">
                <X size={12} /> Cancel
              </Button>
            </>
          )}
          {actions.map((a, i) => (
            <Button key={i} variant={a.variant} disabled={saving} onClick={async () => {
              if (a.status === "Vehicle Assigned") {
                doAction(a.status, { stage_data: { stage: "vehicle_assigned", data: logForm } });
              } else if (a.status === "Under Processing") {
                doAction(a.status, { stage_data: { stage: "warehouse_processing", data: {
                  picked_items: batchData.map(bd => ({
                    sku_id: bd.sku_id, sku_name: bd.sku_name, ordered_qty: bd.ordered_qty,
                    batches: bd.batches, short_reason: bd.short_reason,
                    qty_picked: bd.batches.reduce((sum, b) => sum + (parseInt(b.qty) || 0), 0),
                    // Legacy flat fields for backward compat
                    batch_number: bd.batches[0]?.batch_number || "",
                    mfg_date: bd.batches[0]?.mfg_date || "",
                    expiry_date: bd.batches[0]?.expiry_date || "",
                  })),
                  tentative_dispatch_date: tentativeDispatch
                } } });
              } else if (a.stageKey === "invoice") {
                if (!invoiceForm.invoice_number) { toast.error("Invoice number is required"); return; }
                const invNum = invoiceForm.invoice_number.trim().toLowerCase();
                if (["unknown", "tbd", "pending", "na", "n/a"].includes(invNum) || invNum.length < 3) { toast.error("Please enter a valid invoice number from Tally — not a placeholder"); return; }
                if (!invoiceForm.invoice_date) { toast.error("Invoice date is required"); return; }
                // BATCH 2 (2.9a/b): the amount is mandatory and is compared in whole rupees.
                const invAmt = parseFloat(invoiceForm.invoice_amount);
                if (!invAmt || invAmt <= 0) { toast.error("Invoice amount (incl. GST) is required"); return; }
                if (Math.floor(invAmt) !== Math.floor(o.invoice_value || 0) && !invoiceForm.mismatch_reason) {
                  const r = window.prompt(`Invoice value does not match with order value.\n\nInvoice: ${fmt(invAmt)}\nOrder: ${fmt(o.invoice_value||0)}\n\nEnter a reason to continue:`);
                  if (!r || !r.trim()) { toast.error("A reason is required when the invoice value does not match"); return; }
                  invoiceForm.mismatch_reason = r.trim();
                }
                // 60-day backward validation
                const invDateObj = new Date(invoiceForm.invoice_date);
                const orderCreated = new Date(o.created_at || o.order_date);
                const daysDiff = Math.floor((orderCreated - invDateObj) / 86400000);
                if (daysDiff > 60) {
                  if (!isAdmin) { toast.error(`Invoice date is ${daysDiff} days before order creation. Only Admin can override this. Please verify the date.`); return; }
                  if (!window.confirm(`Invoice date is ${daysDiff} days before order creation date. Are you sure this is correct?`)) return;
                }
                if (!invoiceFile) { toast.error("Invoice PDF is required — please upload the invoice before confirming"); return; }
                // E-way bill is recommended but not mandatory — proceed without it
                // Upload invoice PDF to documents collection
                let uploadOk = false;
                try {
                  const formData = new FormData();
                  formData.append("file", invoiceFile);
                  formData.append("entity_type", "order");
                  formData.append("entity_id", orderId);
                  formData.append("document_type", "invoice_pdf");
                  await api.post("/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
                  uploadOk = true;
                  if (ewayFile) {
                    const ewayFormData = new FormData();
                    ewayFormData.append("file", ewayFile);
                    ewayFormData.append("entity_type", "order");
                    ewayFormData.append("entity_id", orderId);
                    ewayFormData.append("document_type", "eway_pdf");
                    await api.post("/documents/upload", ewayFormData, { headers: { "Content-Type": "multipart/form-data" } });
                  }
                } catch (uploadErr) {
                  toast.error("Invoice PDF upload failed: " + (uploadErr.response?.data?.detail || "Please try again"));
                  return; // Do NOT proceed with transition if upload fails
                }
                doAction(a.status, { stage_data: { stage: "invoice_uploaded", data: invoiceForm } });
              } else if (a.stageKey === "dispatch") {
                if (!dispatchForm.confirmed) { toast.error("Please confirm the dispatch checkbox"); return; }
                const hasOverDispatch = dispatchItems.some(di => di.dispatch_qty > di.ordered_qty);
                if (hasOverDispatch) { toast.error("Cannot dispatch more than ordered quantity"); return; }
                const hasShortNoReason = dispatchItems.some(di => di.dispatch_qty < di.ordered_qty && !di.short_reason);
                if (hasShortNoReason) { toast.error("Please provide a reason for short dispatch"); return; }
                const totalCartons = dispatchItems.reduce((s, di) => s + (di.units_per_master_carton > 0 ? Math.ceil(di.dispatch_qty / di.units_per_master_carton) : 0), 0);
                doAction(a.status, { stage_data: { stage: "dispatched", data: { ...dispatchForm, cartons_loaded: totalCartons, dispatch_items: dispatchItems.map(di => ({
                  sku_id: di.sku_id, sku_name: di.sku_name, ordered_qty_boxes: di.ordered_qty,
                  dispatched_qty_boxes: di.dispatch_qty, cartons: di.units_per_master_carton > 0 ? Math.ceil(di.dispatch_qty / di.units_per_master_carton) : 0,
                  variance: di.dispatch_qty - di.ordered_qty, short_reason: di.short_reason,
                })) } } });
              } else if (a.status === "Delivered") {
                doAction(a.status, { stage_data: { stage: "delivered", data: grnForm } });
              } else if (a.status === "Ready for Dispatch") {
                // Fix: Save batch data before transitioning to Ready for Dispatch
                setSaving(true);
                try {
                  await api.put(`/orders/${orderId}/stage-data`, { stage: "warehouse_processing", data: {
                    picked_items: batchData.map(bd => ({
                      sku_id: bd.sku_id, sku_name: bd.sku_name, ordered_qty: bd.ordered_qty,
                      batches: bd.batches, short_reason: bd.short_reason,
                      qty_picked: bd.batches.reduce((sum, b) => sum + (parseInt(b.qty) || 0), 0),
                      batch_number: bd.batches[0]?.batch_number || "",
                      mfg_date: bd.batches[0]?.mfg_date || "",
                      expiry_date: bd.batches[0]?.expiry_date || "",
                    })),
                    tentative_dispatch_date: tentativeDispatch
                  } });
                } catch (saveErr) {
                  toast.error("Failed to save batch numbers. Please try again.");
                  setSaving(false);
                  return;
                }
                try {
                  const { data: statusResp } = await api.put(`/orders/${orderId}/status`, { status: "Ready for Dispatch", note: actionNote });
                  toast.success("Order updated to Ready for Dispatch");
                  if (statusResp?.sla_result) {
                    const sla = statusResp.sla_result;
                    const icons = { green: "\u2713", amber: "\u26a1", red: "\u2717" };
                    const colors = { green: "background: #f0fdf4; border-left: 4px solid #16a34a; color: #15803d;", amber: "background: #fffbeb; border-left: 4px solid #d97706; color: #92400e;", red: "background: #fef2f2; border-left: 4px solid #dc2626; color: #991b1b;" };
                    const msgs = { green: "You're on time! Keep it up.", amber: "Getting close to the limit.", red: "SLA breached. Try to act faster next time." };
                    setTimeout(() => {
                      toast(`${icons[sla.result]} ${sla.points > 0 ? '+' : ''}${sla.points} points — ${sla.stage}`, { description: `${sla.message}\n${msgs[sla.result]}`, duration: 6000, style: { cssText: colors[sla.result] + " padding: 12px 16px; font-size: 13px; font-weight: 600;" } });
                    }, 500);
                  }
                  loadOrder(); setActionNote("");
                } catch (transErr) {
                  toast.error("Batch numbers saved but transition failed: " + formatApiError(transErr.response?.data?.detail));
                }
                setSaving(false);
              } else {
                doAction(a.status);
              }
            }} className="rounded-sm text-xs h-8" data-testid={`action-${a.status.toLowerCase().replace(/\s+/g,'-')}`}>
              {a.icon && <a.icon size={12} />} {a.label}
            </Button>
          ))}
          {/* Generate PI button moved to standalone prominent position below header */}
        </div>
      </div>

      {/* Prominent Generate PI Button */}
      {["Draft", "Pending with Manager", "Pending with Finance", "Pending with Admin"].includes(s) && (isAdmin || isMgr || roles.includes("Sales Executive")) && (
        <div className="mb-4">
          <Button className="rounded-sm text-xs h-9 bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-pi-btn" onClick={async () => {
            try {
              const response = await api.get(`/orders/${orderId}/proforma-invoice`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
              const a = document.createElement('a'); a.href = url; a.download = `PI-${o.order_number}.pdf`; a.click();
              window.URL.revokeObjectURL(url);
              toast.success("Proforma Invoice downloaded");
            } catch (err) { toast.error(await blobErrorMessage(err, "Failed to generate PI")); }
          }}>
            <FileText size={14} /> Generate Proforma Invoice
          </Button>
        </div>
      )}

      {/* Admin: Cancel + Delete — available at any stage */}
      {isAdmin && s !== "Cancelled" && s !== "Deleted" && (
        <div className="flex gap-2 mb-4" data-testid="admin-order-actions">
          {s !== "Cancelled" && (
            <Button variant="outline" className="rounded-sm text-xs h-8 border-red-300 text-red-600 hover:bg-red-50" data-testid="cancel-order-btn"
              onClick={() => setCancelForm({ open: true, mode: "Cancelled", reason: "", money_action: "", refund_mode: "", refund_reference: "" })}>Cancel Order</Button>
          )}
          <Button variant="outline" className="rounded-sm text-xs h-8 border-red-400 text-red-700 hover:bg-red-50" data-testid="delete-order-btn" onClick={async () => {
            if (!window.confirm(`Permanently delete ${o.order_number}? This cannot be undone.`)) return;
            try { await api.delete(`/orders/${orderId}`); toast.success("Order deleted"); navigate("/orders"); }
            catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
          }}>Delete Order</Button>
        </div>
      )}

      {/* BATCH 2 (2.6b) — cancel with an explicit decision on money already received */}
      <Dialog open={cancelForm.open} onOpenChange={(v) => setCancelForm(f => ({ ...f, open: v }))}>
        <DialogContent className="rounded-sm" data-testid="cancel-order-dialog">
          <DialogHeader><DialogTitle className="text-base">{cancelForm.mode === "Rejected" ? "Reject" : "Cancel"} order {o.order_number}</DialogTitle></DialogHeader>
          {(() => {
            const received = (o.payments||[]).reduce((t,p)=>t+(p.amount||0), 0) || (o.amount_paid||0);
            return (
              <div className="space-y-3">
                {/* FIX-UP R1d — an invoiced or dispatched order says so, and shows the RTO if there is one */}
                {(o.invoice_data?.invoice_number || ["Dispatched","Shipped","Delivered","Closure Requested","Closure Pending","Order Closed"].includes(s) || o.dispatch_data) && (
                  <div className="border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="cancel-after-dispatch-note">
                    <p className="text-xs text-amber-900">
                      This order has been {o.invoice_data?.invoice_number ? `invoiced (${o.invoice_data.invoice_number})` : "dispatched"}.
                      {" "}Raise an RTO first, or enter the reason below (for example &quot;goods lost in transit&quot; or &quot;re-invoiced on ORD-xxxxx&quot;).
                      {" "}Stock is <strong>not</strong> added back &mdash; the RTO receive flow does that when the goods really return.
                    </p>
                    {o.has_rto && <p className="text-[11px] text-amber-800 mt-1" data-testid="cancel-rto-exists">An RTO already exists for this order{o.rto_reversal_rto_number ? ` (${o.rto_reversal_rto_number})` : ""} &mdash; a reason is not required.</p>}
                  </div>
                )}
                <div>
                  <Label className="text-xs">Reason *</Label>
                  <Textarea rows={2} value={cancelForm.reason} onChange={e => setCancelForm(f => ({ ...f, reason: e.target.value }))}
                    className="mt-1 rounded-sm text-xs" placeholder="Why is this order being cancelled?" data-testid="cancel-reason-input" />
                </div>
                {received > 0 && (
                  <div className="border border-amber-300 bg-amber-50 rounded-sm p-3">
                    <p className="text-xs font-semibold text-amber-900 mb-2">{fmt(received)} has already been received. What happens to it?</p>
                    <div className="space-y-1.5">
                      {/* PASS 2 (B2): the corrected copy is the first choice — most of these
                          orders were simply punched wrong. */}
                      <label className="flex items-start gap-2 text-xs cursor-pointer">
                        <input type="radio" name="money_action" checked={cancelForm.money_action === "corrected_copy"} data-testid="cancel-money-corrected-copy"
                          onChange={() => setCancelForm(f => ({ ...f, money_action: "corrected_copy" }))} />
                        <span>Make a corrected copy — a new <strong>Draft</strong> order is created with the same lines, and the money moves to it</span>
                      </label>
                      <label className="flex items-start gap-2 text-xs cursor-pointer">
                        <input type="radio" name="money_action" checked={cancelForm.money_action === "advance_credit"} data-testid="cancel-money-advance"
                          onChange={() => setCancelForm(f => ({ ...f, money_action: "advance_credit" }))} />
                        <span>Keep as advance credit — raises a credit note for this customer</span>
                      </label>
                      <label className="flex items-start gap-2 text-xs cursor-pointer">
                        <input type="radio" name="money_action" checked={cancelForm.money_action === "refund"} data-testid="cancel-money-refund"
                          onChange={() => setCancelForm(f => ({ ...f, money_action: "refund" }))} />
                        <span>Refund to the customer — raises a refund request that an <strong>Admin has to approve</strong> before Finance can pay it</span>
                      </label>
                    </div>
                    {cancelForm.money_action === "refund" && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div><Label className="text-[10px]">Refund mode</Label>
                          <Input value={cancelForm.refund_mode} onChange={e => setCancelForm(f => ({ ...f, refund_mode: e.target.value }))}
                            className="mt-1 h-7 text-xs rounded-sm" placeholder="NEFT / RTGS / UPI / Cheque" data-testid="cancel-refund-mode" /></div>
                        <div><Label className="text-[10px]">Reference / UTR</Label>
                          <Input value={cancelForm.refund_reference} onChange={e => setCancelForm(f => ({ ...f, refund_reference: e.target.value }))}
                            className="mt-1 h-7 text-xs rounded-sm" placeholder="Optional" data-testid="cancel-refund-ref" /></div>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter className="gap-2">
                  <Button variant="outline" className="rounded-sm text-xs h-8" data-testid="cancel-dialog-dismiss"
                    onClick={() => setCancelForm({ open: false, mode: "Cancelled", reason: "", money_action: "", refund_mode: "", refund_reference: "" })}>Keep the order</Button>
                  <Button className="rounded-sm text-xs h-8 bg-red-600 hover:bg-red-700" disabled={cancelling} data-testid="cancel-dialog-confirm"
                    onClick={async () => {
                      if (!cancelForm.reason.trim()) { toast.error("A reason is required"); return; }
                      if (received > 0 && !cancelForm.money_action) { toast.error("Choose what happens to the money received"); return; }
                      setCancelling(true);
                      try {
                        const target = cancelForm.mode === "Rejected" ? "Rejected" : "Cancelled";
                        const { data: resp } = await api.put(`/orders/${orderId}/status`, { status: target, note: cancelForm.reason.trim(),
                          cancel_reason: cancelForm.reason.trim(),
                          money_action: cancelForm.money_action, refund_mode: cancelForm.refund_mode, refund_reference: cancelForm.refund_reference });
                        const what = target === "Rejected" ? "Order rejected" : "Order cancelled";
                        toast.success(cancelForm.money_action === "advance_credit" ? `${what} — advance credit note raised`
                          : cancelForm.money_action === "refund" ? `${what} — refund request waiting for Admin approval`
                          : cancelForm.money_action === "corrected_copy" ? `${what} — corrected copy created${resp?.corrected_copy_number ? ` (${resp.corrected_copy_number})` : ""}`
                          : what);
                        setCancelForm({ open: false, mode: "Cancelled", reason: "", money_action: "", refund_mode: "", refund_reference: "" });
                        loadOrder();
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      finally { setCancelling(false); }
                    }}>{cancelling ? "Saving…" : (cancelForm.mode === "Rejected" ? "Reject this order" : "Cancel this order")}</Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Timeline stepper */}
      <div className="flex items-center gap-0.5 overflow-x-auto pb-2 mb-4" data-testid="status-timeline">
        {STAGES.map((stage, i) => {
          const isPast = historySet.has(stage) || i < curIdx;
          const isCur = stage === s;
          const skip = stage === "Pending with Admin" && !historySet.has(stage) && curIdx > 2;
          if (skip) return null;
          return (
            <div key={stage} className="flex items-center shrink-0">
              <div className={`px-2 py-1 rounded-sm text-[9px] font-semibold ${isCur ? (SC[stage]||'bg-zinc-900 text-white') : isPast ? 'bg-zinc-200 text-zinc-600' : 'bg-zinc-50 text-zinc-300'}`}>
                {isPast && !isCur && <Check size={9} className="inline mr-0.5" />}{stage.replace("Pending with ","").replace("Closure Pending","Closure")}
              </div>
              {i < STAGES.length-1 && <div className={`w-3 h-px mx-0.5 ${isPast?'bg-zinc-400':'bg-zinc-200'}`}/>}
            </div>
          );
        })}
        {s==="Rejected" && <div className="flex items-center shrink-0"><div className="w-3 h-px bg-red-300 mx-0.5"/><div className="px-2 py-1 rounded-sm text-[9px] font-semibold bg-red-100 text-red-800">Rejected</div></div>}
      </div>

      {/* Order info grid */}
      {editMode ? (
        <div className="border border-blue-200 bg-blue-50/30 p-4 rounded-sm mb-4" data-testid="edit-order-form">
          <p className="text-[10px] font-semibold uppercase text-blue-600 mb-3">Editing Order</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label className="text-[10px]">Order Date</Label><Input type="date" value={editForm.order_date} onChange={e => setEditForm({...editForm, order_date: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
            <div><Label className="text-[10px]">Order Type</Label><Input value={editForm.order_type} onChange={e => setEditForm({...editForm, order_type: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
            <div><Label className="text-[10px]">PO Number</Label><Input value={editForm.po_number} onChange={e => setEditForm({...editForm, po_number: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" data-testid="edit-po-number" /></div>
            <div><Label className="text-[10px]">PO Date</Label><Input type="date" value={editForm.po_date} onChange={e => setEditForm({...editForm, po_date: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
            <div className="md:col-span-2"><Label className="text-[10px]">PI Reference</Label><Input value={editForm.pi_reference} onChange={e => setEditForm({...editForm, pi_reference: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
            <div className="md:col-span-2"><Label className="text-[10px]">Special Instructions</Label><Input value={editForm.special_instructions} onChange={e => setEditForm({...editForm, special_instructions: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
          </div>
          {/* Editable Line Items */}
          <div className="mt-4">
            <p className="text-[10px] font-semibold uppercase text-blue-600 mb-2">Line Items</p>
            <div className="border border-zinc-200 overflow-x-auto bg-white rounded-sm">
              <table className="data-table w-full"><thead><tr><th>SKU</th><th>Name</th><th className="num">Qty</th><th className="num">Disc%</th><th className="num">MRP</th><th className="num">Price</th></tr></thead><tbody>
                {editLineItems.map((li, i) => (
                  <tr key={i}>
                    <td className="font-mono text-xs">{li.sku_id}</td>
                    <td className="text-xs">{li.sku_name}</td>
                    <td className="num"><Input type="number" min={1} value={li.quantity} onChange={e => { const items = [...editLineItems]; items[i] = {...items[i], quantity: parseInt(e.target.value)||1}; setEditLineItems(items); }} className="h-7 w-16 text-xs text-right rounded-sm" data-testid={`edit-qty-${i}`} /></td>
                    <td className="num"><Input type="number" step="0.1" min={0} value={li.discount_percent||0} onChange={e => { const items = [...editLineItems]; items[i] = {...items[i], discount_percent: parseFloat(e.target.value)||0}; setEditLineItems(items); }} className="h-7 w-16 text-xs text-right rounded-sm" data-testid={`edit-disc-${i}`} /></td>
                    <td className="num font-mono text-xs">{fmt(li.mrp)}</td>
                    <td className="num font-mono text-xs">{fmt(li.selling_price||li.mrp)}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Customer</p><p className="text-xs font-medium">{o.customer_name}</p></div>
          <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Channel</p><p className="text-xs">{o.channel_name}</p></div>
          <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Date</p><p className="text-xs font-mono">{fmtDate(o.order_date)}</p></div>
          <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Type</p><p className="text-xs">{o.order_type}</p></div>
          <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Created By</p><p className="text-xs">{o.created_by_name}</p></div>
          <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Invoice</p><p className="text-lg font-mono font-bold">{fmt(o.invoice_value)}</p></div>
          {o.dispatch_date && <div><p className="text-[10px] font-semibold uppercase text-amber-600">Dispatch By</p><p className="text-xs font-mono text-amber-700 font-semibold">{fmtDate(o.dispatch_date)}</p></div>}
          {(() => {
            if (o.po_expiry_not_available) return <div><p className="text-[10px] font-semibold uppercase text-zinc-400">PO Expiry</p><p className="text-xs text-zinc-400">N/A</p></div>;
            if (!o.po_expiry_date) return null;
            const past = ["Dispatched","Delivered","Closure Pending","Order Closed"].includes(s);
            if (past) return <div><p className="text-[10px] font-semibold uppercase text-zinc-400">PO Expiry</p><p className="text-xs font-mono text-zinc-400">{fmtDate(o.po_expiry_date)}</p></div>;
            const days = Math.ceil((new Date(o.po_expiry_date) - new Date()) / 86400000);
            const color = days <= 3 ? "text-red-600" : days <= 7 ? "text-amber-600" : "text-green-600";
            const icon = days <= 7 ? " ⚠" : "";
            return <div><p className={`text-[10px] font-semibold uppercase ${color}`}>PO Expiry{icon}</p><p className={`text-xs font-mono font-semibold ${color}`}>{fmtDate(o.po_expiry_date)}{days <= 0 ? ' (expired)' : ` (${days}d)`}</p></div>;
          })()}
          {o.priority && o.priority !== "standard" && <div><p className="text-[10px] font-semibold uppercase text-zinc-400">Priority</p><span className={`px-2 py-0.5 text-[10px] font-bold rounded-sm ${o.priority === "critical" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{o.priority}</span></div>}
        </div>
      )}

      {/* Note input for actions */}
      {actions.length > 0 && (
        <div className="mb-4"><Label className="text-xs">Note / Reason</Label><Textarea value={actionNote} onChange={e=>setActionNote(e.target.value)} placeholder="Required for rejections and discount approvals" className="mt-1 rounded-sm text-xs" rows={2} data-testid="action-note" /></div>
      )}

      {/* Comment Thread */}
      <div className="mb-4 border border-zinc-200 rounded-sm" data-testid="comment-thread">
        <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] font-semibold uppercase text-zinc-400">Comments & Notes</div>
        <div className="max-h-48 overflow-y-auto">
          {(Array.isArray(o.instructions_thread) ? o.instructions_thread : Array.isArray(o.order_notes) ? o.order_notes : []).length === 0 ? (
            <p className="px-3 py-3 text-xs text-zinc-400 text-center">No comments yet</p>
          ) : (Array.isArray(o.instructions_thread) ? o.instructions_thread : Array.isArray(o.order_notes) ? o.order_notes : []).map((n, i) => (
            <div key={i} className="px-3 py-2 border-b border-zinc-50 text-xs" data-testid={`comment-${i}`}>
              <span className="font-semibold text-zinc-700">{n.author || n.section || ''}</span>
              {n.role && <span className="text-zinc-400 ml-1">({n.role})</span>}
              <span className="text-zinc-400 ml-2">{fmtDateTime(n.timestamp)}</span>
              <p className="text-zinc-600 mt-0.5">{n.note}</p>
            </div>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-zinc-200 flex gap-2">
          <Input value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(); } }} placeholder="Add a comment..." className="flex-1 h-8 text-xs rounded-sm" data-testid="comment-input" />
          <Button onClick={postComment} disabled={postingComment || !newComment.trim()} className="rounded-sm h-8 text-xs px-3" data-testid="post-comment-btn">{postingComment ? "..." : "Post"}</Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-3">
        <TabsList className="bg-zinc-100 rounded-sm h-8"><TabsTrigger value="items" className="text-xs rounded-sm h-6">Items</TabsTrigger><TabsTrigger value="payment" className="text-xs rounded-sm h-6">Payment</TabsTrigger><TabsTrigger value="logistics" className="text-xs rounded-sm h-6">Logistics & WH</TabsTrigger><TabsTrigger value="sla" className="text-xs rounded-sm h-6">SLA & Timeline</TabsTrigger><TabsTrigger value="audit" className="text-xs rounded-sm h-6">Audit Trail</TabsTrigger></TabsList>

        {/* ITEMS TAB */}
        <TabsContent value="items">
          {/* BATCH 2 (2.8d) — short dispatch: ordered value vs billed value */}
          {o.is_short_dispatch && (
            <div className="mb-2 flex flex-wrap items-center gap-3 border border-amber-300 bg-amber-50 px-3 py-2 rounded-sm" data-testid="short-dispatch-banner">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-200 px-2 py-0.5 rounded-sm">Short dispatch</span>
              <span className="text-xs text-amber-900">Ordered value <span className="font-mono font-semibold">{fmt(o.ordered_invoice_value || 0)}</span></span>
              <span className="text-xs text-amber-900">Billed value <span className="font-mono font-semibold" data-testid="billed-value">{fmt(o.invoice_value || 0)}</span></span>
            </div>
          )}
          <div className="border border-zinc-200 overflow-x-auto"><table className="data-table w-full"><thead><tr><th>SKU</th><th>Name</th><th>Batch No</th><th className="num">Ordered</th><th className="num">Dispatched</th><th className="num">Short</th><th className="num">MRP</th><th className="num">Disc%</th><th className="num">GST%</th><th className="num">GST Amt</th><th className="num">Net Amt</th></tr></thead><tbody>
            {(o.line_items||[]).map((li,i)=>(<tr key={i} data-testid={`line-row-${i}`}><td className="font-mono text-xs">{li.sku_id}</td><td className="text-xs">{li.sku_name}</td><td className="text-xs font-mono">{li.batch_number || <span className="text-zinc-400">—</span>}</td><td className="num font-mono">{li.quantity}</td><td className="num font-mono" data-testid={`dispatched-qty-${i}`}>{li.dispatched_qty ?? li.quantity}</td><td className={`num font-mono ${li.short_qty ? 'text-amber-700 font-semibold' : 'text-zinc-400'}`} data-testid={`short-qty-${i}`}>{li.short_qty || 0}</td><td className="num font-mono">{fmt(li.mrp||li.selling_price)}</td><td className="num font-mono">{li.discount_percent||0}%</td><td className="num font-mono">{li.gst_percent}%</td><td className="num font-mono">{fmt(li.gst_amount)}</td><td className="num font-mono font-semibold" data-testid={`net-amt-${i}`}>{fmt(li.billed_total ?? li.net_amount)}{li.short_qty ? <span className="block text-[9px] text-zinc-400 font-normal">ordered {fmt(li.net_amount)}</span> : null}</td></tr>))}
          </tbody></table></div>
          {(o.foc_items||[]).length > 0 && <div className="mt-3 border border-dashed border-green-300 bg-green-50 rounded-sm p-3"><p className="text-[10px] font-semibold text-green-700 mb-1">FOC Stock</p>{o.foc_items.map((f,i)=><div key={i} className="text-xs text-green-800">{f.sku_name} x{f.quantity} — FREE {f.reason && <span className="text-green-600 italic">({f.reason})</span>}</div>)}</div>}
          <div className="mt-3 border border-zinc-200 p-3 max-w-sm ml-auto">
            {o.total_discount > 0 && <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">Discount</span><span className="font-mono text-xs text-red-600">-{fmt(o.total_discount)}</span></div>}
            <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">Taxable</span><span className="font-mono text-xs">{fmt(o.taxable_amount)}</span></div>
            {(o.cgst||0)>0 && <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">CGST</span><span className="font-mono text-xs">{fmt(o.cgst)}</span></div>}
            {(o.sgst||0)>0 && <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">SGST</span><span className="font-mono text-xs">{fmt(o.sgst)}</span></div>}
            {(o.igst||0)>0 && <div className="flex justify-between mb-1"><span className="text-xs text-zinc-500">IGST</span><span className="font-mono text-xs">{fmt(o.igst)}</span></div>}
            <div className="flex justify-between pt-2 border-t border-zinc-200"><span className="text-sm font-bold">Invoice Total</span><span className="font-mono text-sm font-bold">{fmt(o.invoice_value)}</span></div>
          </div>
        </TabsContent>

        {/* BATCH 2 (2.5) — changes waiting for Admin approval on this closed order */}
        {changeRequests.length > 0 && (
          <div className="mb-3 border border-amber-300 bg-amber-50 rounded-sm" data-testid="change-requests-panel">
            <p className="px-3 py-2 text-[10px] font-semibold uppercase text-amber-800">Pending Admin approval ({changeRequests.length})</p>
            <table className="data-table w-full"><thead><tr><th>Change</th><th>Reason</th><th>Requested by</th><th>When</th><th></th></tr></thead>
              <tbody>{changeRequests.map((r, i) => (
                <tr key={r._id} data-testid={`change-request-${i}`}>
                  <td className="text-[11px]">{r.type_label}</td>
                  <td className="text-[11px]">{r.reason}</td>
                  <td className="text-[11px]">{r.requested_by_name}</td>
                  <td className="text-[10px] font-mono">{(r.requested_at||'').slice(0,10)}</td>
                  <td className="text-right">
                    {/* FOLLOW-UP 3 (5d): a Finance Manager may decide a same-UTR request, and
                        nobody may decide their own */}
                    {(isAdmin || (isFinMgr && r.type === "same_utr_payment"))
                      && String(r.requested_by || "") !== String(user?._id || "") && (
                      <span className="inline-flex gap-1">
                        <Button size="sm" className="h-6 text-[10px] rounded-sm" data-testid={`approve-change-${i}`}
                          onClick={async () => {
                            try { await api.post(`/order-change-requests/${r._id}/approve`); toast.success("Change applied"); loadOrder(); }
                            catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                          }}>Approve</Button>
                        <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" data-testid={`reject-change-${i}`}
                          onClick={async () => {
                            const note = window.prompt("Reject this change? Note (optional):") ?? "";
                            try { await api.post(`/order-change-requests/${r._id}/reject`, { note }); toast.success("Request rejected — nothing changed"); loadOrder(); }
                            catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                          }}>Reject</Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}</tbody></table>
          </div>
        )}

        {/* PAYMENT TAB — Multi-part payments + deductions */}
        <TabsContent value="payment">
          {/* Payment Info Summary */}
          <div className="border border-zinc-200 p-3 mb-4 text-xs">
            <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Payment Info</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><span className="text-zinc-500 block text-[10px]">Type</span><span className="font-medium">{o.payment_type==='full_payment'?'Full Payment':o.payment_type==='part_advance'?'Part Advance':o.payment_type==='credit_sale'?'Credit Sale':o.payment_type||'—'}</span></div>
              <div><span className="text-zinc-500 block text-[10px]">Invoice Value</span><span className="font-mono font-semibold">{fmt(o.invoice_value)}</span></div>
              <div><span className="text-zinc-500 block text-[10px]">Amount Paid</span><span className="font-mono font-semibold text-green-700">{fmt(o.amount_paid||0)}</span></div>
              <div><span className="text-zinc-500 block text-[10px]">Outstanding</span><span className={`font-mono font-semibold ${(o.outstanding_balance||0)>0?'text-red-600':'text-green-600'}`}>{fmt(o.outstanding_balance||0)}</span></div>
              {!o.due_date && o.due_rule ? <div data-testid="order-due-rule"><span className="text-zinc-500 block text-[10px]">Payment Due Date</span><span className="font-mono font-semibold text-zinc-600">{o.due_rule}</span></div> : null}
              {o.due_date && <div><span className="text-zinc-500 block text-[10px]">Payment Due Date</span><span className={`font-mono font-semibold ${o.payment_status === 'Fully Paid' ? 'text-zinc-400 line-through' : (() => { const d = Math.ceil((new Date(o.due_date) - new Date()) / 86400000); return d < 0 ? 'text-red-600' : d <= 7 ? 'text-amber-600' : 'text-green-600'; })()}`}>{fmtDate(o.due_date)}{o.payment_status !== 'Fully Paid' && (() => { const d = Math.ceil((new Date(o.due_date) - new Date()) / 86400000); return d < 0 ? ` (${Math.abs(d)}d overdue)` : d === 0 ? ' (due today)' : ` (${d}d)` ; })()}</span></div>}
            </div>
            {(o.outstanding_balance||0) <= 0 && o.payment_status !== "Short Paid" && <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-sm"><p className="text-xs font-semibold text-green-800">Payment complete — order fully paid</p></div>}
            {(o.adjusted_amount||0) > 0 && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-sm" data-testid="adjusted-amount-block">
                <p className="text-xs font-semibold text-amber-900">Short paid — adjusted <span className="font-mono" data-testid="adjusted-amount-value">{fmt(o.adjusted_amount)}</span></p>
                <p className="text-[10px] text-amber-800 mt-0.5">{o.adjusted_reason || o.short_payment_closure?.reason || "—"}{o.adjusted_by ? ` · approved by ${o.adjusted_by}` : ""}{o.adjusted_at ? ` on ${fmtDate(o.adjusted_at)}` : ""}</p>
              </div>
            )}
            {o.cancellation_settlement?.action && (
              <div className="mt-2 p-2 bg-zinc-100 border border-zinc-300 rounded-sm" data-testid="cancel-settlement-block">
                <p className="text-xs font-semibold text-zinc-700">Cancelled — {fmt(o.cancellation_settlement.amount)} {o.cancellation_settlement.action === "refund" ? `to be refunded (${o.cancellation_settlement.mode || "mode not set"})` : `kept as advance credit ${o.cancellation_settlement.cn_number || ""}`}</p>
              </div>
            )}
            {o.refund_refused && (
              <div className="mt-2 p-2 bg-red-50 border border-red-300 rounded-sm" data-testid="refund-refused-block">
                <p className="text-xs font-semibold text-red-800">
                  Refund of {fmt(o.refund_refused.amount)} was refused by {o.refund_refused.refused_by} on {fmtDate((o.refund_refused.refused_at || "").slice(0, 10))}: {o.refund_refused.reason}. Choose what happens to this money.
                </p>
                {(isAdmin || perms.includes("refunds.pay")) && (
                  <div className="flex gap-2 mt-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-red-300 text-red-800"
                      onClick={() => settleRefused("advance_credit")} data-testid="refund-refused-advance">Keep as advance credit</Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-red-300 text-red-800"
                      onClick={() => settleRefused("refund")} data-testid="refund-refused-new-refund">Refund to customer (new request)</Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Entries Section */}
          <div className="border border-zinc-200 rounded-sm mb-4">
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase text-zinc-400">Payment Entries ({(o.payments||[]).length})</p>
              {canRecordPayment && (o.outstanding_balance||0) > 0 && !["Draft","Pending with Manager","Pending with Admin","Pending with Finance","Cancelled","Rejected"].includes(s) && (
                <Button variant="outline" className="h-6 text-[10px] px-2 rounded-sm" onClick={() => setPayForm({ amount:"", payment_date: istToday(), payment_mode:"", reference:"", notes:"" })} data-testid="add-payment-entry-btn">+ Add Payment</Button>
              )}
            </div>

            {/* CN Applied */}
            {(o.cn_applications||[]).length > 0 && (
              <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
                <p className="text-[10px] font-semibold text-blue-700 mb-1">Credit Notes Applied</p>
                {o.cn_applications.map((cn, i) => (
                  <div key={i} className="flex justify-between text-xs py-0.5">
                    <span className="font-mono text-blue-800">{cn.cn_number}</span>
                    <span className="font-mono text-blue-700">-{fmt(cn.amount || 0)}</span>
                  </div>
                ))}
              </div>
            )}

            {(o.payments||[]).length === 0 ? (
              <p className="px-3 py-4 text-xs text-zinc-400 text-center">No payments recorded yet</p>
            ) : (
              <table className="data-table w-full"><thead><tr><th>Date</th><th>UTR/Ref</th><th>Mode</th><th className="num">Amount</th><th>Notes</th><th>By</th><th>Proof</th><th>Verified by bank</th></tr></thead>
              <tbody>{(o.payments||[]).map((p,i) => (
                <tr key={i} data-testid={`payment-row-${i}`}><td className="text-[10px] font-mono">{fmtDate(p.date)}</td><td className="font-mono text-[10px]">{p.reference||'—'}</td><td className="text-[10px]">{p.mode}</td><td className="num font-mono text-xs font-semibold text-green-700">{fmt(p.amount)}</td><td className="text-[10px] text-zinc-500">{p.notes||''}</td><td className="text-[10px] text-zinc-500">{p.recorded_by}</td>
                  {/* FOLLOW-UP 3 (12d) — the screenshot, or a badge saying there is none */}
                  <td className="text-[10px]" data-testid={`payment-proof-${i}`}>
                    {(p.proof_files || []).length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {(p.proof_files || []).map((f, fi) => (
                          <a key={fi} href={f.file_url} target="_blank" rel="noreferrer"
                             className="text-blue-700 underline" data-testid={`payment-proof-link-${i}-${fi}`}>
                            {f.file_name || "Proof"}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200"
                            data-testid={`payment-proof-missing-${i}`}>Proof missing</span>
                    )}
                    {canRecordPayment && (
                      <label className="block mt-1 text-blue-700 underline cursor-pointer"
                             data-testid={`payment-proof-add-${i}`}>
                        Add proof
                        <input type="file" accept="image/*,.pdf" multiple className="hidden"
                               onChange={e => { const picked = Array.from(e.target.files || []); e.target.value = ""; addProofToPayment(p.payment_id || `legacy-${i}`, picked); }} />
                      </label>
                    )}
                  </td>
                  <td className="text-[10px]">
                    {canVerifyBank ? (
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={!!p.verified_by_bank} data-testid={`verify-bank-${i}`}
                          onChange={async (e) => {
                            const checked = e.target.checked;   // read BEFORE the await
                            try {
                              await api.post(`/orders/${orderId}/payments/${p.payment_id || `legacy-${i}`}/verify`, { verified: checked });
                              toast.success(checked ? "Marked verified by bank" : "Verification removed");
                              loadOrder();
                            } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                          }} />
                        <span className={p.verified_by_bank ? "text-emerald-700" : "text-amber-700"}>
                          {p.verified_by_bank ? (p.verified_by || "Verified") : "Not verified"}
                        </span>
                      </label>
                    ) : (
                      <span className={p.verified_by_bank ? "text-emerald-700" : "text-amber-700"} data-testid={`verify-state-${i}`}>
                        {p.verified_by_bank ? (p.verified_by || "Verified") : "Not verified"}
                      </span>
                    )}
                  </td></tr>
              ))}</tbody></table>
            )}
            {/* Add Payment Entry Form */}
            {canRecordPayment && ((o.outstanding_balance||0) > 0 || s === "Order Closed") && !["Draft","Pending with Manager","Pending with Admin","Pending with Finance","Cancelled","Rejected"].includes(s) && (
              <div className="p-3 border-t border-zinc-200 bg-zinc-50">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div><Label className="text-[10px]">Amount (INR) *</Label><Input type="number" step="0.01" value={payForm.amount} onChange={e=>setPayForm({...payForm, amount: e.target.value})} className="mt-1 h-7 text-xs rounded-sm font-mono" placeholder={String(o.outstanding_balance||0)} data-testid="pay-record-amount" /></div>
                  <div><Label className="text-[10px]">Date *</Label><Input type="date" value={payForm.payment_date} max={istToday()} onChange={e=>setPayForm({...payForm, payment_date: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
                  <div><Label className="text-[10px]">Mode *</Label><Select value={payForm.payment_mode||"none"} onValueChange={v=>setPayForm({...payForm, payment_mode: v==="none"?"":v})}><SelectTrigger className="mt-1 h-7 text-xs rounded-sm" data-testid="pay-record-mode"><SelectValue placeholder="--" /></SelectTrigger><SelectContent><SelectItem value="none">--</SelectItem>{PAYMENT_MODES.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label className="text-[10px]">UTR/Ref *</Label><Input value={payForm.reference} onChange={e=>setPayForm({...payForm, reference: e.target.value})} className="mt-1 h-7 text-xs rounded-sm font-mono" data-testid="pay-record-ref" /></div>
                  <div><Label className="text-[10px]">Notes</Label><Input value={payForm.notes} onChange={e=>setPayForm({...payForm, notes: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
                </div>
                {/* FOLLOW-UP 3 (12c) / FIX-UP 2 (Y1) — optional for finance, who read the bank statement */}
                <div className="mt-2">
                  <Label className="text-[10px]">Payment proof (screenshot, photo, bank statement)</Label>
                  <Input key={proofInputKey} type="file" accept="image/*,.pdf" multiple
                         onChange={e => setProofFiles(Array.from(e.target.files || []))}
                         className="mt-1 h-7 text-xs rounded-sm" data-testid="pay-record-proof" />
                  <p className="text-[10px] text-zinc-400 mt-0.5">JPG, PNG or PDF, up to 10 MB each. {proofFiles.length > 0 ? `${proofFiles.length} file(s) ready.` : "Flagged as \"Proof missing\" when there is none."}</p>
                </div>
                <Button onClick={recordPayment} disabled={recordingPayment || uploadingProof} className="rounded-sm text-xs h-7 mt-2" data-testid="record-payment-btn">{recordingPayment || uploadingProof ? "Saving..." : "Save Payment Entry"}</Button>
              </div>
            )}
          </div>

          {/* Deductions Section */}
          {(o.blinkit_flags || []).length > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded-sm mb-4 p-3" data-testid="blinkit-flags">
              <p className="text-[10px] font-semibold uppercase text-amber-700 mb-1">What Blinkit's files say</p>
              {(o.blinkit_flags || []).map((f, i) => (
                <p key={i} className="text-[11px] text-amber-800" data-testid={`blinkit-flag-${i}`}>• {f}</p>
              ))}
            </div>
          )}
          <div className="border border-zinc-200 rounded-sm mb-4">
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase text-zinc-400">Deductions ({(o.deductions||[]).length})</p>
              {canAddDeduction && (o.outstanding_balance||0) > 0 && <Button variant="outline" className="h-6 text-[10px] px-2 rounded-sm" onClick={() => setShowDeductForm(!showDeductForm)} data-testid="add-deduction-btn">+ Add Deduction</Button>}
            </div>
            {(o.deductions||[]).length === 0 && !showDeductForm ? (
              <p className="px-3 py-4 text-xs text-zinc-400 text-center">No deductions</p>
            ) : (
              <>
                {(o.deductions||[]).length > 0 && (
                  <table className="data-table w-full"><thead><tr><th>Date</th><th>Type</th><th>Reference</th><th className="num">Amount</th><th>Notes</th><th>By</th><th></th></tr></thead>
                  <tbody>{(o.deductions||[]).map((d,i) => (
                    <tr key={i} className={d.disputed ? 'bg-orange-50' : d.is_tds ? 'bg-amber-50' : ''} data-testid={`deduction-row-${i}`}><td className="text-[10px] font-mono">{fmtDate(d.date)}</td><td className="text-[10px]">{d.deduction_type}{d.is_tds && <span className="ml-1 px-1 py-0.5 text-[8px] bg-amber-100 text-amber-700 rounded-sm">TDS</span>}{d.system && <span className="ml-1 px-1 py-0.5 text-[8px] bg-sky-100 text-sky-700 rounded-sm" data-testid={`deduction-system-${i}`}>From Blinkit file</span>}{d.source === "platform_invoice" && <a href="/platform-invoices" className="ml-1 px-1 py-0.5 text-[8px] bg-orange-100 text-orange-700 rounded-sm underline" data-testid={`deduction-platform-invoice-${i}`}>from Platform Invoice {d.platform_invoice_number || d.reference}</a>}{d.disputed && <span className="ml-1 px-1 py-0.5 text-[8px] bg-orange-100 text-orange-700 rounded-sm" data-testid={`deduction-disputed-${i}`}>Disputed</span>}</td><td className="font-mono text-[10px]">{d.reference||'—'}</td><td className="num font-mono text-xs font-semibold text-red-600">-{fmt(d.amount)}{d.overridden_by && Number(d.original_amount) !== Number(d.amount) && <span className="block text-[10px] font-normal text-zinc-400 line-through" data-testid={`deduction-original-${i}`}>{fmt(d.original_amount)}</span>}</td><td className="text-[10px] text-zinc-500">{d.notes}{d.override_reason && <span className="block text-[9px] text-zinc-400">Changed: {d.override_reason}</span>}{d.dispute_reason && <span className="block text-[9px] text-orange-600">Disputed: {d.dispute_reason}</span>}</td><td className="text-[10px] text-zinc-500">{d.recorded_by}</td><td>{d.system && canAddDeduction && <div className="flex gap-2"><button className="text-[11px] text-zinc-600 hover:text-zinc-900 underline py-1" onClick={() => setOverrideFor({ reference: d.reference, type: d.deduction_type, amount: String(d.amount ?? ""), action: "override", reason: "" })} data-testid={`deduction-override-btn-${i}`}>Change</button>{!d.disputed && <button className="text-[11px] text-orange-600 hover:text-orange-800 underline py-1" onClick={() => setOverrideFor({ reference: d.reference, type: d.deduction_type, amount: String(d.amount ?? ""), action: "dispute", reason: "" })} data-testid={`deduction-dispute-btn-${i}`}>Dispute</button>}</div>}</td></tr>
                  ))}</tbody></table>
                )}
                {overrideFor && (
                  <div className="p-3 border-t border-zinc-200 bg-sky-50/40" data-testid="deduction-override-form">
                    <p className="text-[10px] font-semibold uppercase text-zinc-500 mb-2">
                      {overrideFor.action === "dispute" ? "Mark disputed" : "Change the deducted amount"} — {overrideFor.type}
                    </p>
                    <p className="text-[10px] text-zinc-500 mb-2">{overrideFor.reference}</p>
                    <div className="grid grid-cols-3 gap-2 items-end">
                      {overrideFor.action !== "dispute" && (
                        <div><Label className="text-[10px]">New amount (0 when the claim is won)</Label>
                          <Input type="number" step="0.01" value={overrideFor.amount} className="mt-1 h-7 text-xs rounded-sm"
                                 onChange={e => setOverrideFor({ ...overrideFor, amount: e.target.value })}
                                 data-testid="deduction-override-amount" /></div>
                      )}
                      <div className="col-span-2"><Label className="text-[10px]">Reason *</Label>
                        <Input value={overrideFor.reason} className="mt-1 h-7 text-xs rounded-sm"
                               placeholder={overrideFor.action === "dispute" ? "Why is this being claimed back?" : "Why is the amount being changed?"}
                               onChange={e => setOverrideFor({ ...overrideFor, reason: e.target.value })}
                               data-testid="deduction-override-reason" /></div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button onClick={saveOverride} disabled={savingOverride} className="rounded-sm text-xs h-7" data-testid="deduction-override-save">{savingOverride ? "Saving..." : "Save"}</Button>
                      <Button variant="outline" onClick={() => setOverrideFor(null)} className="rounded-sm text-xs h-7" data-testid="deduction-override-cancel">Cancel</Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {showDeductForm && canAddDeduction && (
              <div className="p-3 border-t border-zinc-200 bg-red-50/30">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div><Label className="text-[10px]">Type *</Label><Select value={deductForm.deduction_type||"none"} onValueChange={v=>setDeductForm({...deductForm, deduction_type: v==="none"?"":v})}><SelectTrigger className="mt-1 h-7 text-xs rounded-sm"><SelectValue placeholder="--" /></SelectTrigger><SelectContent><SelectItem value="none">--</SelectItem>{DEDUCTION_TYPES.filter(t => t !== "Round Off").map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label className="text-[10px]">Amount (INR) *</Label><Input type="number" step="0.01" value={deductForm.amount} onChange={e=>setDeductForm({...deductForm, amount: e.target.value})} className="mt-1 h-7 text-xs rounded-sm font-mono" /></div>
                  <div><Label className="text-[10px]">Reference</Label><Input value={deductForm.reference} onChange={e=>setDeductForm({...deductForm, reference: e.target.value})} className="mt-1 h-7 text-xs rounded-sm font-mono" placeholder="CN#, TDS cert#" /></div>
                  <div><Label className="text-[10px]">Date *</Label><Input type="date" value={deductForm.date} onChange={e=>setDeductForm({...deductForm, date: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
                  <div><Label className="text-[10px]">Notes *</Label><Input value={deductForm.notes} onChange={e=>setDeductForm({...deductForm, notes: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" placeholder="Explain deduction" /></div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button onClick={addDeduction} disabled={addingDeduction} className="rounded-sm text-xs h-7" data-testid="save-deduction-btn">{addingDeduction ? "Saving..." : "Save Deduction"}</Button>
                  <Button variant="ghost" onClick={() => setShowDeductForm(false)} className="rounded-sm text-xs h-7">Cancel</Button>
                </div>
              </div>
            )}
          </div>

          {/* Payment Reconciliation */}
          <div className="border border-zinc-200 p-3 rounded-sm mb-4" data-testid="payment-reconciliation">
            <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Payment Reconciliation</p>
            {(() => {
              const totalPayments = (o.payments||[]).reduce((s,p) => s + (p.amount||0), 0);
              const totalDeductions = (o.deductions||[]).reduce((s,d) => s + (d.amount||0), 0);
              const totalCN = (o.cn_applications||[]).reduce((s,c) => s + (c.amount||0), 0);
              const tdsTotal = (o.deductions||[]).filter(d => d.is_tds).reduce((s,d) => s + (d.amount||0), 0);
              const otherDeductions = totalDeductions - tdsTotal;
              const netReceivable = (o.invoice_value||0) - totalDeductions - totalCN;
              return (
                <div className="space-y-1 text-xs max-w-sm">
                  <div className="flex justify-between"><span className="text-zinc-500">Invoice Total</span><span className="font-mono">{fmt(o.invoice_value)}</span></div>
                  {totalDeductions > 0 && <>
                    <div className="flex justify-between text-red-600"><span>Less: Deductions</span><span className="font-mono">-{fmt(totalDeductions)}</span></div>
                    {tdsTotal > 0 && <div className="flex justify-between pl-4 text-zinc-500"><span>TDS</span><span className="font-mono">-{fmt(tdsTotal)}</span></div>}
                    {otherDeductions > 0 && <div className="flex justify-between pl-4 text-zinc-500"><span>Other Deductions</span><span className="font-mono">-{fmt(otherDeductions)}</span></div>}
                  </>}
                  {totalCN > 0 && <div className="flex justify-between text-blue-600"><span>Less: Credit Notes</span><span className="font-mono">-{fmt(totalCN)}</span></div>}
                  {(totalDeductions > 0 || totalCN > 0) && <div className="flex justify-between border-t border-zinc-200 pt-1"><span className="text-zinc-500">Net Receivable</span><span className="font-mono">{fmt(netReceivable)}</span></div>}
                  <div className="flex justify-between text-green-600"><span>Less: Cash Received</span><span className="font-mono">-{fmt(totalPayments)}</span></div>
                  <div className="flex justify-between border-t border-zinc-200 pt-1 font-semibold"><span>Outstanding</span><span className={`font-mono ${(o.outstanding_balance||0)>0?'text-red-600':'text-green-600'}`}>{fmt(o.outstanding_balance)}</span></div>
                </div>
              );
            })()}
            {/* Round Off & Close */}
            {canRoundOff && (o.outstanding_balance||0) > 0 && (o.outstanding_balance||0) <= (typeof o.outstanding_balance === 'number' && o.outstanding_balance > 0 && o.outstanding_balance <= 1 ? 1 : 100) && (
              <Button variant="outline" onClick={roundOffClose} className="rounded-sm text-xs h-7 mt-3 border-green-300 text-green-700 hover:bg-green-50" data-testid="round-off-btn">Round Off & Close ({fmt(o.outstanding_balance)})</Button>
            )}
          </div>

          {/* Credit Notes Applied */}
          {(o.cn_applications || []).length > 0 && (
            <div className="border border-blue-200 bg-blue-50 p-3 rounded-sm mb-4" data-testid="cn-applied-section">
              <p className="text-[10px] font-semibold uppercase text-blue-700 mb-2">Credit Notes Applied</p>
              {(o.cn_applications || []).map((ca, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-blue-100 last:border-0 text-xs">
                  <span className="font-mono font-semibold">{ca.cn_number}</span>
                  <span className="font-mono text-blue-700">{fmt(ca.amount)}</span>
                  <span className="text-zinc-500">{ca.applied_by} — {fmtDate(ca.applied_at)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Apply Credit Note — Finance + Admin when outstanding > 0 */}
          {(isFin || isAdmin) && (o.outstanding_balance || 0) > 0 && customerCNs.length > 0 && (
            <div className="mt-4 border border-blue-200 bg-blue-50 p-3 rounded-sm" data-testid="apply-cn-section">
              <p className="text-xs font-semibold text-blue-800 mb-2">Apply Credit Note</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px]">Select Credit Note</Label>
                  <Select value={selectedCN?._id || "none"} onValueChange={v => { const cn = customerCNs.find(c => c._id === v); setSelectedCN(cn || null); setCnApplyAmount(cn ? Math.min(cn.balance, o.outstanding_balance) : 0); }}>
                    <SelectTrigger className="mt-0.5 rounded-sm text-xs h-7"><SelectValue placeholder="Select CN" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-- Select --</SelectItem>{customerCNs.map(cn => <SelectItem key={cn._id} value={cn._id}>{cn.cn_number} — {fmt(cn.balance)} available</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {selectedCN && (
                  <>
                    <div><Label className="text-[10px]">Amount to Apply (₹)</Label><Input type="number" step="0.01" value={cnApplyAmount} onChange={e => setCnApplyAmount(Math.min(parseFloat(e.target.value) || 0, selectedCN.balance, o.outstanding_balance))} className="mt-0.5 rounded-sm text-xs h-7" /></div>
                    <div className="flex items-end"><Button className="rounded-sm text-xs h-7" disabled={saving || cnApplyAmount <= 0} onClick={async () => {
                      setSaving(true);
                      try {
                        const { data } = await api.post("/credit-notes/apply", { cn_id: selectedCN._id, order_id: orderId, amount: cnApplyAmount });
                        toast.success(`${fmt(cnApplyAmount)} credit note applied. Outstanding: ${fmt(data.order_outstanding)}`);
                        setSelectedCN(null); setCnApplyAmount(0); loadOrder();
                        api.get(`/credit-notes/customer/${o.customer_id}?status=Open,Partially Used`).then(r => setCustomerCNs(r.data || [])).catch(err => console.error("OrderDetailPage error:", err));
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      finally { setSaving(false); }
                    }} data-testid="apply-cn-btn">Apply CN</Button></div>
                  </>
                )}
              </div>
            </div>
          )}


          {/* Short Payment Closure Banner (if exists) */}
          {o.short_payment_closure && (
            <div className={`mt-4 p-3 border rounded-sm ${o.short_payment_closure.status === 'approved' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`} data-testid="short-payment-banner">
              <p className="text-xs font-semibold text-red-800 mb-1">Short Payment Closure — {fmt(o.short_payment_closure.short_amount)} deducted</p>
              <p className="text-[10px] text-zinc-600">Reason: {o.short_payment_closure.deduction_reason}</p>
              <p className="text-[10px] text-zinc-600">Details: {o.short_payment_closure.details}</p>
              <p className="text-[10px] text-zinc-500 mt-1">Requested by: {o.short_payment_closure.requested_by_name}{o.short_payment_closure.approved_by_name ? ` · Approved by: ${o.short_payment_closure.approved_by_name}` : ' · Pending approval'}</p>
              {o.short_payment_closure.status === 'pending' && (isFinMgr || isAdmin) && (
                <Button className="rounded-sm text-xs h-7 mt-2" onClick={async () => {
                  try { await api.post(`/orders/${orderId}/approve-short-closure`); toast.success("Short payment closure approved"); loadOrder(); }
                  catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                }} data-testid="approve-short-closure-btn"><Check size={12} /> Approve Closure</Button>
              )}
            </div>
          )}

          {/* Short Payment Closure Button + Form */}
          {["Delivered", "Closure Pending"].includes(s) && (isFin || isAdmin) && o.outstanding_balance > 0 && !o.short_payment_closure && (
            <>
              {!showShortPay ? (
                <Button variant="outline" className="rounded-sm text-xs h-8 mt-4 border-red-300 text-red-700 hover:bg-red-50" onClick={() => setShowShortPay(true)} data-testid="short-payment-btn">Close with Short Payment</Button>
              ) : (
                <div className="mt-4 border border-red-200 bg-red-50 p-4 rounded-sm space-y-3" data-testid="short-payment-form">
                  <p className="text-xs font-semibold text-red-800">Short Payment Closure</p>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div><span className="text-zinc-500">Invoice Total</span><p className="font-mono font-semibold">{fmt(o.invoice_value)}</p></div>
                    <div><span className="text-zinc-500">Total Received</span><p className="font-mono font-semibold text-green-600">{fmt(o.invoice_value - (o.outstanding_balance || 0))}</p></div>
                    <div><span className="text-zinc-500">Short Amount</span><p className="font-mono font-semibold text-red-600">{fmt(o.outstanding_balance)}</p></div>
                  </div>
                  <div>
                    <Label className="text-[10px]">Deduction Reason *</Label>
                    <Select value={shortPayForm.deduction_reason || "none"} onValueChange={v => setShortPayForm({...shortPayForm, deduction_reason: v === "none" ? "" : v})}>
                      <SelectTrigger className="mt-1 rounded-sm text-xs" data-testid="deduction-reason"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">-- Select --</SelectItem>{(getValues("payment_deduction_reasons") || []).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[10px]">Deduction Reference Number (optional)</Label><Input value={shortPayForm.deduction_reference} onChange={e => setShortPayForm({...shortPayForm, deduction_reference: e.target.value})} className="mt-1 h-7 text-xs rounded-sm" placeholder="Platform deduction note #" /></div>
                  <div><Label className="text-[10px]">Details / Comments *</Label><Textarea value={shortPayForm.details} onChange={e => setShortPayForm({...shortPayForm, details: e.target.value})} className="mt-1 rounded-sm text-xs" rows={3} placeholder="Explain what was deducted and why..." /></div>
                  <div className="flex gap-2">
                    <Button className="rounded-sm text-xs h-7" disabled={saving} onClick={async () => {
                      if (!shortPayForm.deduction_reason) { toast.error("Deduction reason is required"); return; }
                      if (!shortPayForm.details) { toast.error("Details are required"); return; }
                      setSaving(true);
                      try { await api.post(`/orders/${orderId}/short-payment-closure`, shortPayForm); toast.success("Short payment closure requested"); setShowShortPay(false); loadOrder(); }
                      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      finally { setSaving(false); }
                    }} data-testid="submit-short-payment">Submit for Approval</Button>
                    <Button variant="outline" className="rounded-sm text-xs h-7" onClick={() => setShowShortPay(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Invoice & E-Way Bill Section — moved here from Logistics tab (Fix 8) */}
          {["Vehicle Assigned", "Invoice Uploaded", "Dispatched", "Delivered", "Closure Pending", "Order Closed"].includes(s) && (
            <div className={`mt-4 border p-4 rounded-sm ${s === "Vehicle Assigned" && canInvoice ? 'border-violet-200 bg-violet-50' : 'border-zinc-200'}`} data-testid="invoice-upload-form">
              <p className="text-xs font-semibold text-violet-800 mb-3">Invoice & E-Way Bill</p>
              {s === "Vehicle Assigned" && canInvoice ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div><Label className="text-[10px]">Tally Invoice Number *</Label><Input value={invoiceForm.invoice_number} onChange={e=>setInvoiceForm({...invoiceForm,invoice_number:e.target.value})} className="mt-1 h-7 text-xs rounded-sm font-mono" data-testid="invoice-number" /></div>
                    <div><Label className="text-[10px]">Invoice amount (incl. GST) *</Label><Input type="number" step="0.01" value={invoiceForm.invoice_amount} onChange={e=>setInvoiceForm({...invoiceForm,invoice_amount:e.target.value})} placeholder={String(o.invoice_value||0)} className="mt-1 h-7 text-xs rounded-sm font-mono" data-testid="invoice-amount" />
                      <p className="text-[9px] text-zinc-400 mt-0.5">Order value: {fmt(o.invoice_value||0)}</p></div>
                    <div><Label className="text-[10px]">Invoice Date *</Label><Input type="date" value={invoiceForm.invoice_date} max={istToday()} onChange={e=>setInvoiceForm({...invoiceForm,invoice_date:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" data-testid="invoice-date" /></div>
                    <div><Label className="text-[10px]">Upload Invoice PDF *</Label><Input type="file" accept=".pdf" onChange={e=>setInvoiceFile(e.target.files?.[0]||null)} className="mt-1 h-7 text-xs rounded-sm" data-testid="invoice-pdf-upload" /></div>
                  </div>
                  {ewayRecommended ? (
                    <div className="border border-amber-200 bg-amber-50 p-3 rounded-sm">
                      <p className="text-[10px] text-amber-700 mb-2">This order&apos;s taxable value is &ge; &#8377;50,000 &mdash; an e-way bill is recommended. You can upload it now or later.</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div><Label className="text-[10px]">E-Way Bill Number (recommended)</Label><Input value={invoiceForm.eway_number} onChange={e=>setInvoiceForm({...invoiceForm,eway_number:e.target.value})} className="mt-1 h-7 text-xs rounded-sm font-mono" data-testid="eway-number" /></div>
                        <div><Label className="text-[10px]">Validity Date (recommended)</Label><Input type="date" value={invoiceForm.eway_validity} onChange={e=>setInvoiceForm({...invoiceForm,eway_validity:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
                        <div><Label className="text-[10px]">Upload E-Way Bill PDF (recommended)</Label><Input type="file" accept=".pdf,image/*" onChange={e=>setEwayFile(e.target.files?.[0]||null)} className="mt-1 h-7 text-xs rounded-sm" data-testid="eway-pdf-upload" /></div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-xs text-zinc-700">
                  {o.invoice_data?.invoice_number ? (
                    <div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                        <div><span className="text-zinc-400">Invoice #:</span> <span className="font-mono font-semibold">{o.invoice_data.invoice_number}</span></div>
                        <div><span className="text-zinc-400">Date:</span> {fmtDate(o.invoice_data.invoice_date)}</div>
                        {o.invoice_data.eway_number && <div><span className="text-zinc-400">E-Way Bill:</span> <span className="font-mono">{o.invoice_data.eway_number}</span></div>}
                        {o.invoice_data.uploaded_by && <div><span className="text-zinc-400">Uploaded by:</span> {o.invoice_data.uploaded_by} on {fmtDate(o.invoice_data.uploaded_at)}</div>}
                      </div>
                      <div className="flex gap-2 flex-wrap items-center">
                        <Button variant="outline" className="rounded-sm text-xs h-7" data-testid="download-invoice-btn" onClick={async () => {
                          try {
                            const r = await api.get(`/orders/${orderId}/invoice-download`, { responseType: 'blob' });
                            if (r.data.size < 100) { toast.error("Invoice PDF is empty or corrupted. Please re-upload."); return; }
                            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                            const a = document.createElement('a'); a.href = url; a.download = `Invoice-${o.order_number}.pdf`; a.click();
                            window.URL.revokeObjectURL(url);
                          } catch (err) {
                            const detail = err.response?.data;
                            let msg = "Invoice PDF not available.";
                            if (detail && typeof detail === 'object') {
                              try { const txt = await detail.text(); const parsed = JSON.parse(txt); msg = parsed.detail || msg; } catch (err) { console.error("OrderDetailPage error:", err); }
                            }
                            toast.error(msg + " Use the 'Upload Invoice PDF' button to upload.");
                          }
                        }}><Download size={12} /> Download Invoice</Button>
                        {o.invoice_data.eway_number && (
                          <Button variant="outline" className="rounded-sm text-xs h-7" onClick={async () => {
                            try {
                              const r = await api.get(`/orders/${orderId}/eway-download`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                              const a = document.createElement('a'); a.href = url; a.download = `EwayBill-${o.order_number}.pdf`; a.click();
                              window.URL.revokeObjectURL(url);
                            } catch { toast.error("E-Way Bill PDF not available"); }
                          }}><Download size={12} /> Download E-Way Bill</Button>
                        )}
                        {/* Upload / Re-upload option — visible to Finance + Admin at all post-Vehicle stages */}
                        {(canInvoice || isAdmin) && (
                          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-dashed border-violet-300 bg-violet-50 rounded-sm text-xs text-violet-700 hover:bg-violet-100 cursor-pointer font-medium" data-testid="upload-invoice-pdf-btn">
                            <Upload size={12} /> Upload Invoice PDF
                            <input type="file" accept=".pdf" className="hidden" onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const formData = new FormData();
                                formData.append("file", file);
                                formData.append("entity_type", "order");
                                formData.append("entity_id", orderId);
                                formData.append("document_type", "invoice_pdf");
                                await api.post("/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
                                toast.success("Invoice PDF uploaded successfully. Download is now available.");
                                loadOrder();
                              } catch (err) { toast.error("Upload failed — " + (err.response?.data?.detail || "try again")); }
                            }} />
                          </label>
                        )}
                      </div>
                    </div>
                  ) : <p className="text-zinc-400">Invoice not yet uploaded</p>}
                </div>
              )}
            </div>
          )}

        </TabsContent>

        {/* LOGISTICS & WAREHOUSE TAB */}
        <TabsContent value="logistics">
          {/* PART C (C4) — the signed delivery copy lives with the delivery */}
          <PODBlock orderId={o._id} />
          {/* Delivery Address — visible to Logistics and Warehouse */}
          {o.delivery_address && (
            <div className="border border-blue-200 bg-blue-50 p-3 mb-3 rounded-lg" data-testid="delivery-address-display">
              <p className="text-[10px] font-semibold uppercase text-blue-700 mb-1">Delivery Address</p>
              <p className="text-sm font-medium text-zinc-900">{o.delivery_address.label || "Default"}</p>
              <p className="text-sm text-zinc-700">{o.delivery_address.line1}{o.delivery_address.city ? `, ${o.delivery_address.city}` : ""}{o.delivery_address.state ? `, ${o.delivery_address.state}` : ""} {o.delivery_address.pincode || ""}</p>
              {o.delivery_address.contact_person && <p className="text-xs text-zinc-500 mt-1">Contact: {o.delivery_address.contact_person} {o.delivery_address.contact_phone || ""}</p>}
            </div>
          )}

          {/* Warehouse processing info */}
          {o.warehouse_processing && <div className="border border-zinc-200 p-3 mb-3"><p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Warehouse Processing</p><p className="text-xs text-zinc-700">Processed by: {o.warehouse_processing.processed_by} &middot; {fmtDateTime(o.warehouse_processing.processed_at)}</p>{o.warehouse_processing.tentative_dispatch_date && <p className="text-xs">Tentative dispatch: {fmtDate(o.warehouse_processing.tentative_dispatch_date)}</p>}</div>}

          {/* Batch entry form — ONLY at Under Processing stage */}
          {s === "Under Processing" && (isWh || isAdmin) && (
            <div className="border border-orange-200 bg-orange-50 p-4 rounded-sm mb-3" data-testid="warehouse-batch-form">
              <p className="text-xs font-semibold text-orange-800 mb-3">Warehouse Processing — Enter Batch Details</p>
              {batchData.map((bd, skuIdx) => {
                const totalPicked = bd.batches.reduce((sum, b) => sum + (parseInt(b.qty) || 0), 0);
                const isShort = totalPicked > 0 && totalPicked < bd.ordered_qty;
                const isOver = totalPicked > bd.ordered_qty;
                return (
                <div key={skuIdx} className="border border-orange-300 bg-white rounded-sm p-3 mb-3" data-testid={`batch-sku-${skuIdx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div><p className="text-xs font-semibold text-zinc-900">{bd.sku_name}</p><p className="text-[10px] text-zinc-500">Ordered: {bd.ordered_qty} units</p></div>
                    <div className={`text-xs font-mono font-semibold ${isOver ? 'text-red-600' : isShort ? 'text-amber-600' : 'text-green-600'}`}>
                      Total picked: {totalPicked} of {bd.ordered_qty}
                    </div>
                  </div>
                  {bd.batches.map((batch, bIdx) => (
                    <div key={bIdx} className="grid grid-cols-6 gap-2 mb-1 items-end" data-testid={`batch-row-${skuIdx}-${bIdx}`}>
                      {/* TODO: Make batch_number mandatory when warehouse processes are more organised. */}
                      <div><Label className="text-[10px]">Batch No</Label><Input value={batch.batch_number} onChange={e=>{const d=[...batchData];d[skuIdx].batches[bIdx].batch_number=e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,'');setBatchData(d)}} className="h-7 text-xs rounded-sm" data-testid={`batch-no-${skuIdx}-${bIdx}`} placeholder="Optional" /></div>
                      <div><Label className="text-[10px]">MFD</Label><Input type="date" value={batch.mfg_date} onChange={e=>{const d=[...batchData];d[skuIdx].batches[bIdx].mfg_date=e.target.value;setBatchData(d)}} className="h-7 text-xs rounded-sm" /></div>
                      <div><Label className="text-[10px]">Expiry</Label><Input type="date" value={batch.expiry_date} onChange={e=>{const d=[...batchData];d[skuIdx].batches[bIdx].expiry_date=e.target.value;setBatchData(d)}} className="h-7 text-xs rounded-sm" /></div>
                      <div><Label className="text-[10px]">Qty from batch</Label><Input type="number" min={0} value={batch.qty} onChange={e=>{const d=[...batchData];d[skuIdx].batches[bIdx].qty=parseInt(e.target.value)||0;setBatchData(d)}} className="h-7 text-xs rounded-sm text-right" data-testid={`batch-qty-${skuIdx}-${bIdx}`} /></div>
                      <div><Label className="text-[10px]">Comment</Label><Input value={batch.comment||""} onChange={e=>{const d=[...batchData];d[skuIdx].batches[bIdx].comment=e.target.value;setBatchData(d)}} className="h-7 text-xs rounded-sm" placeholder="Optional" /></div>
                      <div className="flex items-end gap-1">
                        {bd.batches.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={()=>{const d=[...batchData];d[skuIdx].batches.splice(bIdx,1);setBatchData(d)}}><X size={12}/></Button>}
                      </div>
                    </div>
                  ))}
                  <Button variant="ghost" className="h-6 text-[10px] text-orange-700 mt-1 px-2" onClick={()=>{const d=[...batchData];d[skuIdx].batches.push({batch_number:"",mfg_date:"",expiry_date:"",qty:0,comment:""});setBatchData(d)}} data-testid={`add-batch-${skuIdx}`}>+ Add Batch</Button>
                  {isOver && <div className="mt-1 p-1.5 bg-red-100 border border-red-200 rounded-sm text-[10px] text-red-700">Cannot pick more than ordered quantity ({bd.ordered_qty})</div>}
                  {isShort && (
                    <div className="mt-1 p-1.5 bg-amber-50 border border-amber-200 rounded-sm">
                      <p className="text-[10px] text-amber-700 mb-1">Short shipment: {bd.ordered_qty - totalPicked} units short — reason required</p>
                      <select value={bd.short_reason||""} onChange={e=>{const d=[...batchData];d[skuIdx].short_reason=e.target.value;setBatchData(d)}} className="h-7 text-xs rounded-sm border border-amber-300 bg-white w-full" data-testid={`short-reason-${skuIdx}`}>
                        <option value="">-- Select Reason --</option>
                        {SHORT_SHIPMENT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );})}
              <div className="mt-3"><Label className="text-[10px]">Tentative Dispatch Date <span className="text-red-500">*</span></Label><Input type="date" value={tentativeDispatch} onChange={e=>setTentativeDispatch(e.target.value)} className="h-7 text-xs rounded-sm w-40 mt-1" data-testid="tentative-dispatch" /></div>
            </div>
          )}

          {/* Read-only batch display at Ready for Dispatch and later stages */}
          {["Ready for Dispatch","Vehicle Assigned","Invoice Uploaded","Dispatched","Delivered","Closure Pending","Order Closed"].includes(s) && (
            <div className="border border-zinc-200 bg-zinc-50 p-4 rounded-sm mb-3" data-testid="batch-readonly-display">
              <p className="text-xs font-semibold text-zinc-500 mb-2">Batch Details (recorded at processing)</p>
              {(o.warehouse_processing?.picked_items || []).length > 0 ? (
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-zinc-200 text-zinc-400">
                    <th className="text-left py-1 pr-2 font-medium">SKU</th>
                    <th className="text-left py-1 pr-2 font-medium">Batch No</th>
                    <th className="text-left py-1 pr-2 font-medium">MFD</th>
                    <th className="text-left py-1 pr-2 font-medium">Expiry</th>
                    <th className="text-right py-1 pr-2 font-medium">Ordered</th>
                    <th className="text-right py-1 pr-2 font-medium">Picked</th>
                    <th className="text-right py-1 pr-2 font-medium">Variance</th>
                    <th className="text-left py-1 font-medium">Comment</th>
                  </tr></thead>
                  <tbody>
                    {o.warehouse_processing.picked_items.map((pi, idx) => {
                      const orderedQty = pi.ordered_qty || (o.line_items || []).find(li => li.sku_id === pi.sku_id)?.quantity || 0;
                      const batches = (pi.batches && pi.batches.length > 0 && pi.batches.some(b => b.batch_number))
                        ? pi.batches
                        : pi.batch_number ? [{ batch_number: pi.batch_number, mfg_date: pi.mfg_date || "", expiry_date: pi.expiry_date || "", qty: pi.qty_picked || orderedQty || "", comment: "" }] : [];
                      const totalPicked = batches.reduce((s, b) => s + (parseInt(b.qty) || 0), 0);
                      const variance = totalPicked - orderedQty;
                      if (batches.length === 0) return (
                        <tr key={idx} className="border-b border-zinc-100"><td className="py-1 pr-2 font-medium">{pi.sku_name || pi.sku_id}</td><td colSpan={7} className="py-1 text-zinc-400 italic">No batch data recorded</td></tr>
                      );
                      return batches.map((b, bi) => (
                        <tr key={`${idx}-${bi}`} className={`border-b border-zinc-100 ${variance < 0 && bi === 0 ? 'bg-amber-50' : ''}`}>
                          {bi === 0 && <td rowSpan={batches.length} className="py-1 pr-2 font-medium align-top">{pi.sku_name || pi.sku_id}</td>}
                          <td className="py-1 pr-2 font-mono">{b.batch_number || "—"}</td>
                          <td className="py-1 pr-2">{b.mfg_date || "—"}</td>
                          <td className="py-1 pr-2">{b.expiry_date || "—"}</td>
                          {bi === 0 && <td rowSpan={batches.length} className="py-1 pr-2 text-right font-mono align-top">{orderedQty}</td>}
                          <td className="py-1 pr-2 text-right font-mono">{b.qty ?? "—"}</td>
                          {bi === 0 && <td rowSpan={batches.length} className={`py-1 pr-2 text-right font-mono font-semibold align-top ${variance < 0 ? 'text-red-600' : variance === 0 ? 'text-green-600' : 'text-blue-600'}`}>{variance === 0 ? '—' : (variance > 0 ? '+' : '') + variance}</td>}
                          <td className="py-1 text-zinc-500">{b.comment || ""}</td>
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-zinc-400 italic" data-testid="no-batch-data">No batch data recorded</p>
              )}
            </div>
          )}

          {/* Vehicle & transport details */}
          {o.logistics && <div className="border border-zinc-200 p-3 mb-3"><p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Transport & Dispatch Details</p><div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-zinc-700"><div><span className="text-zinc-400 block text-[10px]">Transporter</span><span className="font-medium" data-testid="transport-transporter">{o.logistics.transporter || o.logistics.transporter_name || "—"}</span></div><div><span className="text-zinc-400 block text-[10px]">Vehicle No</span><span className="font-medium font-mono" data-testid="transport-vehicle">{o.logistics.vehicle || o.logistics.vehicle_number || "—"}</span></div><div><span className="text-zinc-400 block text-[10px]">Transport Type</span>{o.logistics.transport_type || "—"}</div><div><span className="text-zinc-400 block text-[10px]">LR / Docket No</span><span className="font-mono">{o.logistics.lr_number || "—"}</span></div><div><span className="text-zinc-400 block text-[10px]">Freight Terms</span>{o.logistics.freight_terms || "—"}</div><div><span className="text-zinc-400 block text-[10px]">Dispatch By</span><span className="font-mono">{fmtDate(o.dispatch_date || o.logistics.dispatch_date) || "—"}</span></div>{o.logistics.total_weight ? <div><span className="text-zinc-400 block text-[10px]">Total Weight</span><span className="font-semibold">{o.logistics.total_weight} kg</span></div> : null}{o.logistics.total_boxes ? <div><span className="text-zinc-400 block text-[10px]">Total Boxes</span>{o.logistics.total_boxes}</div> : null}{o.logistics.est_delivery ? <div><span className="text-zinc-400 block text-[10px]">Est. Delivery</span>{fmtDate(o.logistics.est_delivery)}</div> : null}</div></div>}

          {/* Warehouse Processing Details */}
          {o.warehouse_processing && <div className="border border-zinc-200 p-3 mb-3"><p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Warehouse Processing</p><div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-zinc-700"><div><span className="text-zinc-400 block text-[10px]">Processed By</span><span className="font-medium">{o.warehouse_processing.processed_by || "—"}</span></div><div><span className="text-zinc-400 block text-[10px]">Processed At</span>{fmtDate(o.warehouse_processing.processed_at) || "—"}</div>{o.warehouse_processing.tentative_dispatch_date ? <div><span className="text-zinc-400 block text-[10px]">Tentative Dispatch</span><span className="font-mono">{fmtDate(o.warehouse_processing.tentative_dispatch_date)}</span></div> : null}{o.warehouse_processing.grn_number ? <div><span className="text-zinc-400 block text-[10px]">GRN #</span><span className="font-mono font-semibold">{o.warehouse_processing.grn_number}</span></div> : null}{o.warehouse_processing.total_weight ? <div><span className="text-zinc-400 block text-[10px]">Total Weight</span><span className="font-semibold">{o.warehouse_processing.total_weight} kg</span></div> : null}{o.warehouse_processing.total_boxes ? <div><span className="text-zinc-400 block text-[10px]">Total Boxes</span>{o.warehouse_processing.total_boxes}</div> : null}</div></div>}

          {/* Dispatch Data */}
          {o.dispatch_data && <div className="border border-zinc-200 p-3 mb-3"><p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Dispatch Summary</p><div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-zinc-700"><div><span className="text-zinc-400 block text-[10px]">Dispatched By</span><span className="font-medium">{o.dispatch_data.dispatched_by || "—"}</span></div><div><span className="text-zinc-400 block text-[10px]">Dispatched At</span>{fmtDate(o.dispatch_data.dispatched_at) || "—"}</div><div><span className="text-zinc-400 block text-[10px]">Actual Dispatch Date</span><span className="font-mono">{fmtDate(o.dispatch_data.actual_dispatch_date || o.dispatch_data.dispatch_date) || "—"}</span></div><div><span className="text-zinc-400 block text-[10px]">Vehicle No</span><span className="font-mono">{o.dispatch_data.vehicle_number || o.dispatch_data.vehicle || (o.logistics||{}).vehicle_number || "—"}</span></div>{o.dispatch_data.seal_number ? <div><span className="text-zinc-400 block text-[10px]">Seal Number</span>{o.dispatch_data.seal_number}</div> : null}{o.dispatch_data.cartons_loaded ? <div><span className="text-zinc-400 block text-[10px]">Cartons Loaded</span>{o.dispatch_data.cartons_loaded}</div> : null}{o.dispatch_data.notes ? <div className="md:col-span-2"><span className="text-zinc-400 block text-[10px]">Notes</span>{o.dispatch_data.notes}</div> : null}</div></div>}

          {/* Logistics Cost Summary — GST breakdown */}
          {["Ready for Dispatch","Vehicle Assigned","Invoice Uploaded","Dispatched","Delivered","Closure Pending","Order Closed"].includes(s) && (
            <div className="border border-zinc-200 p-3 mb-3 rounded-sm" data-testid="logistics-cost-summary">
              <p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Logistics Cost</p>
              <div className="space-y-2 text-xs">
                {/* Row 1: Estimated Freight */}
                <div className="flex items-baseline gap-2">
                  <span className="text-zinc-500 w-32 shrink-0">Estimated Freight:</span>
                  {o.estimated_freight_total ? (
                    <div>
                      <span className="font-mono font-semibold">
                        {o.estimated_freight_base && o.estimated_freight_gst_rate ? (
                          <>{fmt(o.estimated_freight_base)} + {fmt(o.estimated_freight_gst_amount || 0)} ({o.estimated_freight_gst_rate}% GST) = {fmt(o.estimated_freight_total)}</>
                        ) : (
                          fmt(o.estimated_freight_total)
                        )}
                      </span>
                      {o.estimated_freight_entered_by && <p className="text-[9px] text-zinc-400">by {o.estimated_freight_entered_by}</p>}
                    </div>
                  ) : <span className="text-zinc-400">— Not set</span>}
                </div>
                {/* Row 2: Actual Freight */}
                <div className="flex items-baseline gap-2">
                  <span className="text-zinc-500 w-32 shrink-0">Actual Freight:</span>
                  <span className="font-mono">{o.actual_freight_total ? fmt(o.actual_freight_total) : '— (pending)'}</span>
                </div>
                {/* Row 3: Chargeable Weight */}
                <div className="flex items-baseline gap-2">
                  <span className="text-zinc-500 w-32 shrink-0">Chargeable Weight:</span>
                  <span className="font-mono">{o.chargeable_weight_kg ? `${o.chargeable_weight_kg} kg` : '—'}</span>
                </div>
                {/* Row 4: Cost % of Invoice */}
                <div className="flex items-baseline gap-2">
                  <span className="text-zinc-500 w-32 shrink-0">Cost % of Invoice:</span>
                  {(() => { const freight = o.actual_freight_total || o.estimated_freight_total || 0; const iv = o.invoice_value || 1; const pct = freight > 0 ? parseFloat((freight / iv * 100).toFixed(1)) : 0;
                    return freight > 0 ? (
                      <span className={`font-mono font-semibold ${pct > 100 ? 'text-red-600' : pct > 15 ? 'text-amber-600' : 'text-green-600'}`}>{pct > 999 ? ">999%" : `${pct}%`}{!o.actual_freight_total && o.estimated_freight_total ? ' (est.)' : ''}</span>
                    ) : <span className="text-zinc-400">—</span>;
                  })()}
                </div>
                {/* Variance */}
                {o.estimated_freight_total > 0 && o.actual_freight_total > 0 && (() => {
                  const v = ((o.actual_freight_total - o.estimated_freight_total) / o.actual_freight_total * 100).toFixed(1);
                  return <p className={`text-[10px] font-mono ${Math.abs(v) <= 10 ? 'text-green-600' : Math.abs(v) <= 15 ? 'text-amber-600' : 'text-red-600'}`}>Variance: {v > 0 ? '+' : ''}{v}%</p>;
                })()}
              </div>
              {/* Edit form — Logistics roles + Admin, only when no actual freight */}
              {(isLog || isAdmin) && !o.actual_freight_total && ["Vehicle Assigned","Invoice Uploaded"].includes(s) && (
                <div className="mt-3 border-t border-zinc-100 pt-3" data-testid="freight-edit-form">
                  <p className="text-[10px] font-semibold text-zinc-500 mb-2">{o.estimated_freight_total ? 'Edit Estimated Freight' : 'Enter Estimated Freight'}</p>
                  <div className="flex gap-2 items-end flex-wrap">
                    <div>
                      <Label className="text-[10px]">Base Amount (INR)</Label>
                      <Input type="number" step="0.01" defaultValue={o.estimated_freight_base || ""} id="est-freight-base" className="mt-1 h-7 w-32 text-xs rounded-sm font-mono" placeholder="e.g. 5000" />
                    </div>
                    <div>
                      <Label className="text-[10px]">GST %</Label>
                      <select id="est-freight-gst" defaultValue={o.estimated_freight_gst_rate ?? 18} className="mt-1 h-7 w-20 text-xs rounded-sm border border-zinc-200 px-1">
                        {(getValues("logistics_gst_rates") || []).map(r => <option key={r} value={r}>{r}%</option>)}
                      </select>
                    </div>
                    {o.estimated_freight_total > 0 && (
                      <div>
                        <Label className="text-[10px]">Edit Reason *</Label>
                        <select id="est-freight-reason" className="mt-1 h-7 w-44 text-xs rounded-sm border border-zinc-200 px-1">
                          <option value="">-- Select reason --</option>
                          {(getValues("freight_edit_reasons") || []).map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    )}
                    <Button variant="outline" className="h-7 text-[10px] px-3 rounded-sm" data-testid="save-est-freight" onClick={async () => {
                      const base = parseFloat(document.getElementById("est-freight-base")?.value);
                      const gstRate = parseInt(document.getElementById("est-freight-gst")?.value) || 0;
                      const reason = document.getElementById("est-freight-reason")?.value || "";
                      if (!base || base <= 0) { toast.error("Enter a valid base amount"); return; }
                      if (o.estimated_freight_total > 0 && !reason) { toast.error("Edit reason is required"); return; }
                      try { const { data } = await api.patch(`/orders/${orderId}/logistics-cost`, { base_rupees: base, gst_rate: gstRate, edit_reason: reason }); toast.success("Logistics cost saved"); setOrder(data); }
                      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}>Save</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Fix 6: Logistics vehicle assignment form */}
          {s === "Ready for Dispatch" && (isLog || isAdmin) && (
            <div className="border border-teal-200 bg-teal-50 p-4 rounded-sm mb-3" data-testid="vehicle-form">
              <p className="text-xs font-semibold text-teal-800 mb-3">Assign Vehicle</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-[10px]">Transporter</Label><Input value={logForm.transporter} onChange={e=>setLogForm({...logForm,transporter:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" data-testid="log-transporter" /></div>
                <div><Label className="text-[10px]">Vehicle Number</Label><Input value={logForm.vehicle} onChange={e=>setLogForm({...logForm,vehicle:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" data-testid="log-vehicle" /></div>
                <div><Label className="text-[10px]">Transport Type</Label>
                  <Select value={logForm.transport_type||"none"} onValueChange={v=>setLogForm({...logForm,transport_type:v==="none"?"":v})}>
                    <SelectTrigger className="mt-1 h-7 text-xs rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">--</SelectItem>{TRANSPORT_TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px]">Estimated Freight (INR)</Label><Input type="number" value={logForm.freight_cost} onChange={e=>setLogForm({...logForm,freight_cost:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" data-testid="log-freight-cost" />
                  {!(parseFloat(logForm.freight_cost) > 0) && <p className="text-[10px] text-amber-600 mt-0.5" data-testid="freight-estimate-warning">No estimated freight — this order will show as freight Missing in PnL until the vendor bill is uploaded.</p>}</div>
                <div><Label className="text-[10px]">Freight Terms</Label>
                  <Select value={logForm.freight_terms||"none"} onValueChange={v=>setLogForm({...logForm,freight_terms:v==="none"?"":v})}>
                    <SelectTrigger className="mt-1 h-7 text-xs rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">--</SelectItem>{FREIGHT_TERMS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px]">LR / Docket Number</Label><Input value={logForm.lr_number} onChange={e=>setLogForm({...logForm,lr_number:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
                <div><Label className="text-[10px]">Dispatch Date</Label><Input type="date" value={logForm.dispatch_date} onChange={e=>setLogForm({...logForm,dispatch_date:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
                <div><Label className="text-[10px]">Est. Delivery Date</Label><Input type="date" value={logForm.est_delivery} onChange={e=>setLogForm({...logForm,est_delivery:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
              </div>
            </div>
          )}

          {/* Invoice section moved to Payment tab (Fix 8) */}

          {/* ═══ Dispatch Confirmation Form (Invoice Uploaded stage) ═══ */}
          {s === "Invoice Uploaded" && (
            <div className={`border p-4 rounded-sm mb-3 ${canDispatch ? 'border-teal-200 bg-teal-50' : 'border-zinc-200'}`} data-testid="dispatch-confirm-form">
              <p className="text-xs font-semibold text-teal-800 mb-3">Dispatch Confirmation</p>
              {canDispatch ? (
                <div className="space-y-3">
                  {/* Per-SKU Dispatch Table */}
                  <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto" data-testid="dispatch-items-table">
                    <table className="data-table w-full">
                      <thead><tr><th>SKU</th><th className="num">Ordered</th><th className="num">Units/Ctn</th><th className="num">Dispatch Qty</th><th className="num">Cartons</th><th className="num">Ctn Wt (kg)</th><th className="num">Total Wt (kg)</th><th>Variance</th><th>Short Reason</th></tr></thead>
                      <tbody>
                        {dispatchItems.map((di, idx) => {
                          const cartons = di.units_per_master_carton > 0 ? Math.ceil(di.dispatch_qty / di.units_per_master_carton) : null;
                          const variance = di.dispatch_qty - di.ordered_qty;
                          const totalWeight = cartons && di.carton_weight > 0 ? (cartons * di.carton_weight).toFixed(1) : null;
                          return (
                            <tr key={idx} data-testid={`dispatch-item-${idx}`}>
                              <td className="text-xs">{di.sku_name}</td>
                              <td className="num font-mono">{di.ordered_qty}</td>
                              <td className="num font-mono">{di.units_per_master_carton > 0 ? di.units_per_master_carton : <span className="text-amber-600">N/A</span>}</td>
                              <td><Input type="number" min={0} value={di.dispatch_qty} onChange={e => { const d = [...dispatchItems]; d[idx].dispatch_qty = parseInt(e.target.value) || 0; setDispatchItems(d); }} className="h-7 w-20 text-xs text-right rounded-sm ml-auto" data-testid={`dispatch-qty-${idx}`} /></td>
                              <td className="num font-mono font-semibold">{cartons !== null ? cartons : <span className="text-amber-500">N/A</span>}</td>
                              <td className="num font-mono text-xs">{di.carton_weight > 0 ? di.carton_weight : <span className="text-amber-500 text-[10px]">N/A</span>}</td>
                              <td className="num font-mono text-xs font-semibold">{totalWeight || <span className="text-amber-500 text-[10px]">N/A</span>}</td>
                              <td className="text-xs">
                                {variance === 0 && <span className="text-green-600 font-semibold">Exact</span>}
                                {variance < 0 && <span className="text-red-600 font-semibold">Short by {Math.abs(variance)}</span>}
                                {variance > 0 && <span className="text-amber-600 font-semibold">Extra {variance}</span>}
                              </td>
                              <td>{variance < 0 && <Input value={di.short_reason} onChange={e => { const d = [...dispatchItems]; d[idx].short_reason = e.target.value; setDispatchItems(d); }} placeholder="Reason required" className="h-7 text-xs rounded-sm w-32" data-testid={`dispatch-short-reason-${idx}`} />}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-200 text-xs font-semibold text-zinc-700">
                      Total cartons: <span className="font-mono">{dispatchItems.reduce((s, di) => s + (di.units_per_master_carton > 0 ? Math.ceil(di.dispatch_qty / di.units_per_master_carton) : 0), 0)}</span> across {dispatchItems.length} SKUs
                      {' · '} Estimated weight: <span className="font-mono">{dispatchItems.reduce((s, di) => { const c = di.units_per_master_carton > 0 ? Math.ceil(di.dispatch_qty / di.units_per_master_carton) : 0; return s + (di.carton_weight > 0 ? c * di.carton_weight : 0); }, 0).toFixed(1)} kg</span>
                      {dispatchItems.some(di => di.units_per_master_carton <= 0) && <span className="text-amber-600 ml-2">(some SKUs missing carton config)</span>}
                      {dispatchItems.some(di => !di.carton_weight || di.carton_weight <= 0) && <span className="text-amber-600 ml-2">(weight not configured for some SKUs)</span>}
                    </div>
                  </div>
                  {dispatchItems.some(di => di.dispatch_qty > di.ordered_qty) && <div className="p-2 bg-red-100 border border-red-200 rounded-sm text-xs text-red-700">Cannot dispatch more than ordered quantity</div>}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div><Label className="text-[10px]">Actual Dispatch Date *</Label><Input type="date" value={dispatchForm.dispatch_date} onChange={e=>setDispatchForm({...dispatchForm,dispatch_date:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" data-testid="dispatch-date" /></div>
                    <div>
                      <Label className="text-[10px]">Vehicle Number</Label>
                      <Input value={dispatchForm.vehicle || (o.logistics||{}).vehicle_number || ""} onChange={e=>setDispatchForm({...dispatchForm,vehicle:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" />
                    </div>
                    <div><Label className="text-[10px]">Seal Number (optional)</Label><Input value={dispatchForm.seal_number} onChange={e=>setDispatchForm({...dispatchForm,seal_number:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
                  </div>
                  <div><Label className="text-[10px]">Dispatch Photo (optional)</Label><Input type="file" accept="image/*" className="mt-1 h-7 text-xs rounded-sm" /></div>
                  <label className="flex items-center gap-2 cursor-pointer pt-1">
                    <input type="checkbox" checked={dispatchForm.confirmed} onChange={e=>setDispatchForm({...dispatchForm,confirmed:e.target.checked})} className="rounded-sm" data-testid="dispatch-confirm-check" />
                    <span className="text-xs text-zinc-700 font-medium">I confirm this order has been fully loaded and the vehicle has departed</span>
                  </label>
                </div>
              ) : (
                <p className="text-xs text-zinc-500">Waiting for Warehouse/Logistics team to confirm dispatch...</p>
              )}
            </div>
          )}

          {/* Fix 6: GRN / Delivery form */}
          {s === "Dispatched" && (isLog || isAdmin) && (
            <div className="border border-emerald-200 bg-emerald-50 p-4 rounded-sm mb-3" data-testid="grn-form">
              <p className="text-xs font-semibold text-emerald-800 mb-3">GRN / Delivery Confirmation</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-[10px]">GRN / Docket Number</Label><Input value={grnForm.grn_number} disabled={grnLocked && !isAdmin} onChange={e=>setGrnForm({...grnForm,grn_number:e.target.value})} className="mt-1 h-7 text-xs rounded-sm disabled:bg-zinc-100" data-testid="grn-number" /></div>
                <div><Label className="text-[10px]">GRN / Delivery Date</Label><Input type="date" max={istToday()} value={grnForm.delivery_date} disabled={grnLocked && !isAdmin} onChange={e=>setGrnForm({...grnForm,delivery_date:e.target.value})} className="mt-1 h-7 text-xs rounded-sm disabled:bg-zinc-100" data-testid="grn-delivery-date" /></div>
                {grnLocked && !isAdmin && <div className="col-span-2"><p className="text-[10px] text-zinc-500" data-testid="grn-locked-note">The GRN / Delivery Date is locked once saved. Ask an Admin to change it.</p></div>}
                {grnLocked && isAdmin && <div className="col-span-2"><Label className="text-[10px]">Reason for changing the saved GRN / Delivery Date *</Label><Input value={grnForm.change_reason || ""} onChange={e=>setGrnForm({...grnForm,change_reason:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" placeholder="Why is it being corrected?" data-testid="grn-change-reason" /></div>}
                <div><Label className="text-[10px]">Delivered To</Label><Input value={grnForm.delivered_to} onChange={e=>setGrnForm({...grnForm,delivered_to:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
              </div>
              <div className="mt-2"><Label className="text-[10px]">Remarks</Label><Input value={grnForm.remarks} onChange={e=>setGrnForm({...grnForm,remarks:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
            </div>
          )}

          {/* BATCH 3 FIX-UP (F3a): Admin correction once the order is past Dispatched */}
          {isAdmin && ["Delivered", "Closure Requested", "Closure Pending", "Short Paid"].includes(s) && (
            grnCorrecting ? (
              <div className="border border-amber-300 bg-amber-50 p-4 rounded-sm mb-3" data-testid="grn-correct-form">
                <p className="text-xs font-semibold text-amber-900 mb-3">Correct GRN / Delivery Date</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><Label className="text-[10px]">GRN / Docket Number</Label><Input value={grnForm.grn_number} onChange={e=>setGrnForm({...grnForm,grn_number:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" data-testid="grn-correct-number" /></div>
                  <div><Label className="text-[10px]">GRN / Delivery Date</Label><Input type="date" max={istToday()} value={grnForm.delivery_date} onChange={e=>setGrnForm({...grnForm,delivery_date:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" data-testid="grn-correct-date" /></div>
                  <div><Label className="text-[10px]">Delivered To</Label><Input value={grnForm.delivered_to} onChange={e=>setGrnForm({...grnForm,delivered_to:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" /></div>
                </div>
                <div className="mt-2"><Label className="text-[10px]">Reason for the correction *</Label><Input value={grnForm.change_reason || ""} onChange={e=>setGrnForm({...grnForm,change_reason:e.target.value})} className="mt-1 h-7 text-xs rounded-sm" placeholder="Why is it being corrected?" data-testid="grn-correct-reason" /></div>
                <p className="text-[10px] text-amber-800 mt-2">The order stays {s}. The correction is audit-logged and the due date is recalculated.</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="text-xs" disabled={grnSaving} onClick={saveGrnCorrection} data-testid="grn-correct-save">{grnSaving ? "Saving..." : "Save correction"}</Button>
                  <Button size="sm" variant="outline" className="text-xs" onClick={()=>{ setGrnForm(f => ({ ...f, change_reason: "" })); setGrnCorrecting(false); }} data-testid="grn-correct-cancel">Cancel</Button>
                </div>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="text-xs mb-3" onClick={()=>setGrnCorrecting(true)} data-testid="grn-correct-btn">Correct GRN / Delivery Date</Button>
            )
          )}

          {/* Delivery data if exists */}
          {o.delivery_data && <div className="border border-zinc-200 p-3 mb-3"><p className="text-[10px] font-semibold uppercase text-zinc-400 mb-2">Delivery</p><div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-zinc-700"><div><span className="text-zinc-400 block text-[10px]">GRN / Docket No</span><span className="font-mono font-semibold">{o.delivery_data.grn_number || "—"}</span></div><div><span className="text-zinc-400 block text-[10px]">Delivery Date</span>{fmtDate(o.delivery_data.delivery_date) || "—"}</div><div><span className="text-zinc-400 block text-[10px]">Delivered To</span>{o.delivery_data.delivered_to || "—"}</div>{o.delivery_data.remarks ? <div><span className="text-zinc-400 block text-[10px]">Remarks</span>{o.delivery_data.remarks}</div> : null}</div></div>}

          {/* Invoice data (show when uploaded) */}
          {/* Invoice details moved to Payment tab (Fix 8) */}

          {/* Notes already shown in unified comment thread above */}
        </TabsContent>

        {/* AUDIT TRAIL TAB */}
        {/* SLA & Timeline Tab */}
        <TabsContent value="sla">
          {slaData ? (
            <div className="space-y-3" data-testid="sla-timeline">
              {slaData.po_received_at && <p className="text-xs text-zinc-600 mb-2">PO received from Gmail: <span className="font-mono font-semibold">{fmtDateTime(slaData.po_received_at)}</span> (immutable server timestamp)</p>}
              {slaData.late_punch_reason && <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm mb-2 text-xs"><span className="font-semibold text-amber-800">Late Punch Reason:</span> {slaData.late_punch_reason}</div>}
              {slaData.fuzzy_match_flag && <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm mb-2 text-xs text-amber-800">Fuzzy PO match — document uploaded for verification</div>}
              <div className="border border-zinc-200 overflow-x-auto">
                <table className="data-table w-full">
                  <thead><tr><th>Stage</th><th>Entered At</th><th>Completed By</th><th className="num">Working Time</th><th className="num">SLA Limit</th><th>Status</th><th>Deadline</th></tr></thead>
                  <tbody>
                    {(slaData.timeline || []).map((t, i) => {
                      const ragColors = { green: "bg-green-100 text-green-700", amber: "bg-amber-100 text-amber-700", red: "bg-red-100 text-red-700", grey: "bg-zinc-100 text-zinc-500" };
                      const ragIcons = { green: "✅", amber: "⚠️", red: "🔴", grey: "⬜" };
                      const hrs = Math.floor(t.working_mins / 60);
                      const mins = t.working_mins % 60;
                      const thrHrs = Math.floor(t.threshold_mins / 60);
                      const thrMins = t.threshold_mins % 60;
                      return (
                        <tr key={i} className={t.is_current ? "bg-blue-50" : ""} data-testid={`sla-stage-${i}`}>
                          <td className="text-xs font-medium">{t.stage} {t.is_current && <span className="text-[9px] text-blue-600 ml-1">(current)</span>}</td>
                          <td className="text-[10px] text-zinc-500">{fmtDateTime(t.entered_at)}</td>
                          <td className="text-[10px] text-zinc-500">{t.completed_by || (t.is_current ? "—" : "")}</td>
                          <td className="num font-mono text-xs">{hrs > 0 ? `${hrs}h ` : ""}{mins}m</td>
                          <td className="num font-mono text-xs text-zinc-500">{thrHrs > 0 ? `${thrHrs}h ` : ""}{thrMins}m</td>
                          <td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${ragColors[t.rag]}`}>{ragIcons[t.rag]} {t.rag.toUpperCase()}</span></td>
                          <td className="text-[10px] text-zinc-500">{t.deadline ? fmtDateTime(t.deadline) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-center py-8 text-zinc-400 text-xs">SLA data loading...</p>
          )}
        </TabsContent>

        <TabsContent value="audit">
          <div className="space-y-2">{(o.status_history||[]).map((sh,i)=>(
            <div key={i} className="flex items-start gap-3 py-2 border-b border-zinc-50">
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${SC[sh.status]?.split(' ')[0]||'bg-zinc-400'}`}/>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap"><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${SC[sh.status]||''}`}>{sh.status}</span>{sh.previous_status && <span className="text-[10px] text-zinc-400">from {sh.previous_status}</span>}</div>
                <p className="text-[10px] text-zinc-500 mt-0.5">{sh.user_name} ({sh.role||'-'}) &middot; {fmtDateTime(sh.timestamp)}</p>
                {sh.note && <p className="text-xs text-zinc-700 mt-0.5 italic">"{sh.note}"</p>}
              </div>
            </div>
          ))}</div>
        </TabsContent>
      </Tabs>

      {/* RTOs linked to this order */}
      <div className="mt-6 border border-zinc-200 rounded-sm" data-testid="order-rtos-section">
        <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase text-zinc-400">Returns (RTO) for this Order</span>
          <Button variant="outline" className="rounded-sm h-6 text-[10px] px-2" onClick={() => navigate(`/returns?create=true&order_id=${orderId}`)} data-testid="create-rto-for-order">
            <RotateCcw size={10} /> Create RTO
          </Button>
        </div>
        {orderRTOs.length === 0 ? (
          <p className="px-4 py-3 text-xs text-zinc-400 text-center">No RTOs for this order</p>
        ) : (
          <div className="divide-y divide-zinc-50">
            {orderRTOs.map(rto => (
              <button key={rto._id} onClick={() => navigate(`/returns/${rto._id}`)} className="w-full px-4 py-2 flex items-center gap-4 hover:bg-zinc-50 text-left" data-testid={`order-rto-${rto._id}`}>
                <span className="font-mono text-xs font-semibold w-24">{rto.rto_number}</span>
                <span className="px-1 py-0.5 text-[8px] font-bold bg-red-100 text-red-700 rounded-sm">RTO</span>
                <span className="text-xs text-zinc-600 flex-1">{rto.return_type} — {rto.return_reason}</span>
                <span className="font-mono text-xs">{rto.total_boxes_returned} boxes</span>
                <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${{"Draft":"bg-zinc-200 text-zinc-700","Approval Requested":"bg-amber-100 text-amber-800","Approved":"bg-blue-100 text-blue-800","Stock Received":"bg-teal-100 text-teal-800","Closed":"bg-green-100 text-green-800","Rejected":"bg-red-100 text-red-800"}[rto.status]||''}`}>{rto.status}</span>
                {rto.pnl_impact && <span className="font-mono text-xs text-red-600">{new Intl.NumberFormat('en-IN',{minimumFractionDigits:2}).format(rto.pnl_impact)}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    
      {/* FOLLOW-UP 3 (5c) — a second payment under the same UTR goes for approval */}
      <Dialog open={sameUtr.open} onOpenChange={(v) => setSameUtr(s => ({ ...s, open: v }))}>
        <DialogContent className="max-w-lg" data-testid="same-utr-dialog">
          <DialogHeader><DialogTitle className="text-base">This UTR is already on this order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-700" data-testid="same-utr-message">{sameUtr.message}</p>
            {sameUtr.existing && (
              <div className="border border-zinc-200 bg-zinc-50 p-2 rounded-sm text-xs" data-testid="same-utr-existing">
                Already recorded: {fmt(sameUtr.existing.amount)} on {sameUtr.existing.date} by {sameUtr.existing.recorded_by}
              </div>
            )}
            <div>
              <Label className="text-xs">Why is this a second payment under the same UTR? <span className="text-red-500">*</span></Label>
              <Textarea className="rounded-sm text-xs" rows={3} data-testid="same-utr-reason"
                        value={sameUtr.reason}
                        onChange={(e) => setSameUtr(s => ({ ...s, reason: e.target.value }))}
                        placeholder="At least 10 characters" />
            </div>
            <p className="text-[11px] text-zinc-500">Nothing is recorded until an Admin or a Finance Manager approves it.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="rounded-sm text-xs"
                    onClick={() => setSameUtr({ open: false, message: "", existing: null, reason: "" })}>Cancel</Button>
            <Button size="sm" className="rounded-sm text-xs" disabled={recordingPayment}
                    data-testid="same-utr-send" onClick={sendSameUtrForApproval}>Send for approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FOLLOW-UP 3 (8b) — Admin changes who gets the sales credit */}
      <Dialog open={repDialog.open} onOpenChange={(v) => setRepDialog(s => ({ ...s, open: v }))}>
        <DialogContent className="max-w-md" data-testid="sales-rep-dialog">
          <DialogHeader><DialogTitle className="text-base">Change sales rep</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Sales rep <span className="text-red-500">*</span></Label>
              <select className="w-full border border-zinc-300 rounded-sm h-9 text-xs px-2"
                      data-testid="sales-rep-select" value={repDialog.rep_id}
                      onChange={(e) => setRepDialog(s => ({ ...s, rep_id: e.target.value }))}>
                <option value="">-- Select --</option>
                {salesUsers.map(u => (
                  <option key={u._id || u.id} value={u._id || u.id}>
                    {u.name}{u.email ? ` — ${u.email}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Reason <span className="text-red-500">*</span></Label>
              <Textarea className="rounded-sm text-xs" rows={2} data-testid="sales-rep-reason"
                        value={repDialog.reason}
                        onChange={(e) => setRepDialog(s => ({ ...s, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="rounded-sm text-xs"
                    onClick={() => setRepDialog({ open: false, rep_id: "", reason: "" })}>Cancel</Button>
            <Button size="sm" className="rounded-sm text-xs" data-testid="sales-rep-save"
                    onClick={saveSalesRep}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
</div>
  );
}

export default function OrderDetailPage() {
  return <OrderDetailErrorBoundary><OrderDetailPageInner /></OrderDetailErrorBoundary>;
}
